import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import Box from "@mui/material/Box";

import Gameboard from "../../components/gameboard";
import GoalMountain from "../../components/gameboard/GoalMountain";
import SoftProfile from "../../components/ProfilePictures/SoftProfile";
import Trophy3D from "../../components/Trophy3D/Trophy3D";
import Crown3D from "../../components/Crown3D/Crown3D";
import TVWorkspaceSwitcher from "./TVWorkspaceSwitcher";
import TVDashboardScene from "./TVDashboardScene";
import { GET_DASHBOARDS, GET_DASHBOARD } from "../CompanyMetrics/dashboards/dashboardStore";
import { useCompanyGoal, useCompanyKpiTotal, periodLabel } from "../../hooks/companyGoal";

import {
  UseGroupAllKpis,
  UseGroupKpis,
  UseTopPlayers,
  useGetPreviousKpis,
  filterKpisByUserScores,
  isExcludedFromBoards,
} from "../../hooks/kpi";
import { recomputeScopedPlayerScores } from "../../helpers/kpiPoints";
import { isKpiGamified, filterKpisForPublicDisplay } from "../../constants/kpi";
import {
  UseCompany,
  UseSelectedWorkspace,
  UseCompanyWorkspaces,
  UseYetiStats,
  UseUsersFullData,
} from "../../hooks/company";
import { refreshKpis } from "../../redux/kpi/actions";
import { formatNumber } from "../../helpers/formatter";
import { formatKpiPercent } from "../../helpers/scoreAggregate";

import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import PauseIcon from "@mui/icons-material/Pause";
import GMBLogo from "../../img/main_logo.png";
import { getUserTeam } from "../UserManagement/userTeamStore";
import { getTeamColor } from "../UserManagement/teamColorStore";
import { getTeams } from "../../redux/teams/actions";
import { UseTeams } from "../../hooks/teams";

const BACK_TO_HOME = "company-view";
const SCENE_DURATION_MS = 45000;
// Poll often enough that a Scorekeeper edit lands on the TV within ~15s.
// The TV is on a separate device from the editor, so it can't piggyback on the
// in-tab Redux optimistic update like /home does — it has to refetch.
const REFRESH_INTERVAL_MS = 15 * 1000;
const PERIODS = ["today", "yesterday", "week", "lastWeek", "month", "lastMonth"];
const PERIOD_LABELS = { today: "Today", yesterday: "Yesterday", week: "This Week", lastWeek: "Last Week", month: "This Month", lastMonth: "Last Month" };

/* ---------- Team assignment ----------
   Real teams now come from `player.team` (set via Player Management
   → Customize view → Manage teams). Players without a team show as
   "Unassigned". The OFFICE_ACCENT palette below is the fallback color
   ramp for any team name we haven't pre-styled. */
// Graphite + teal TV: lead with teal, and no pure web-blue in the ramp (the
// seed blue clashed with the graphite stage). Admin-picked team colors still
// win over this via getTeamColor().
const TEAM_PALETTE = ["#5684d6", "#E16C8B", "#16A34A", "#F59E0B", "#A855F7", "#5FB0A8", "#EC4899"];
const OFFICE_ACCENT = {
  Kitsap: "#5684d6",
  Seattle: "#E16C8B",
};
const teamColorFor = (name) => {
  if (!name) return "#64748B";
  // Unassigned isn't a real team — keep it a flat, pale slate rather than
  // letting it land on a random palette color that reads as if it were one.
  if (name === "Unassigned") return "#64748B";
  // 1) Admin-picked color from the Manage Teams modal — highest priority.
  const stored = getTeamColor(name);
  if (stored) return stored;
  // 2) Legacy office accents kept for backwards-compat with seed names.
  if (OFFICE_ACCENT[name]) return OFFICE_ACCENT[name];
  // 3) Deterministic fallback so a never-styled team name still gets a
  //    stable, repeatable color across scenes and refreshes.
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return TEAM_PALETTE[Math.abs(hash) % TEAM_PALETTE.length];
};
// Read the team a player belongs to.
// Priority order:
//   1) Yeti gets its own dedicated label so it always shows on the leaderboard
//      instead of being filtered out as "Unassigned".
//   2) the local userTeamStore override — admin's most recent assignment via
//      Player Management. Wins over the backend so any stale `team` value
//      (including a legacy default like "Unassigned") never blocks the new pick.
//   3) the player's `team` field from the backend, when it's a real string.
//   4) "Unassigned" fallback so every player still groups somewhere on the TV.
const YETI_TEAM = "The Yeti";
const teamForPlayer = (player) => {
  if (player?.isYeti) return YETI_TEAM;
  const local = getUserTeam(player?.userId, "");
  if (local) return local;
  const t = player?.team;
  if (typeof t === "string" && t.trim() && t.trim() !== "Unassigned") return t.trim();
  return "Unassigned";
};

/** Mirrors the home Leaderboard's "Hide" admin action (Show/Hide tab on each
 *  player's doughnut chart → user.isActive=false) and archived players
 *  (user.isArchived=true). Hidden/archived players are excluded from every TV
 *  roster and aggregation so they don't show up in team standings, workspace
 *  totals, or KPI sums. Yeti always passes — it has no isActive/isArchived
 *  field and is a fixed benchmark, not a player. */
const isVisiblePlayer = (p) =>
  p?.isYeti || (!p?.isArchived && p?.isActive !== false);

/* ---------- Design tokens ---------- */
const FONT = `'Space Grotesk', 'DM Sans', 'Inter', system-ui, -apple-system, sans-serif`;
// Mono font reserved for titles (SectionHeader / SceneHeader) — gives the
// TV that "broadcast / live readout" console feel without affecting body.
const MONO_FONT = `'Space Grotesk', 'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace`;

const tokens = (dark) => ({
  font: FONT,
  bg: dark
    // Graphite + teal stage: a soft teal glow bleeding down from the top and a
    // fainter deep-teal pool at the base, over a near-black graphite ground.
    // Dark only; light mode stays clean.
    ? "radial-gradient(110% 85% at 50% -16%, rgba(92,146,232,0.24) 0%, transparent 60%), radial-gradient(80% 60% at 50% 122%, rgba(64,96,168,0.13) 0%, transparent 55%), linear-gradient(180deg, #151b28 0%, #0b0e15 100%)"
    : "radial-gradient(60% 60% at 50% 40%, #F4F6FB 0%, #E6EAF2 100%)",
  surface: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.85)",
  surfaceStrong: dark ? "rgba(255,255,255,0.08)" : "#FFFFFF",
  border: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
  hairline: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
  text: dark ? "#ECEDED" : "#0B1320",
  textMuted: dark ? "rgba(236,237,237,0.62)" : "rgba(11,19,32,0.55)",
  textFaint: dark ? "rgba(236,237,237,0.38)" : "rgba(11,19,32,0.38)",
  accent: dark ? "#5684d6" : "#3A5FC0",
  // Platinum, not gold — the graphite stage carries no warm gold; the leader
  // reads metallic instead.
  gold: dark ? "#D8DDE3" : "#8A8F98",
  silver: "#B8C0CC",
  bronze: "#C58A5A",
  positive: "#3CC68A",
  negative: "#E16C6C",
});


/* ---------- Atoms ---------- */
const Label = ({ children, sx, ...rest }) => (
  <Box
    component="span"
    sx={{
      fontFamily: FONT,
      fontWeight: 500,
      fontSize: "1.15vh",
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

const Display = ({ children, sx, ...rest }) => (
  <Box
    component="span"
    sx={{
      fontFamily: FONT,
      fontWeight: 500,
      fontFeatureSettings: '"tnum" 1, "lnum" 1',
      letterSpacing: "-0.02em",
      lineHeight: 1,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

const Text = ({ children, sx, weight = 500, ...rest }) => (
  <Box
    component="span"
    sx={{
      fontFamily: FONT,
      fontWeight: weight,
      letterSpacing: "0",
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

const WorkspaceBadge = ({ name, dark }) => {
  if (!name) return null;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: "2.6vh",
        px: "0.9vw",
        borderRadius: "999px",
        background: dark ? "rgba(86,132,214,0.14)" : "rgba(86,132,214,0.10)",
        color: dark ? "#A8C0F0" : "#3768C4",
        fontFamily: FONT,
        fontWeight: 500,
        fontSize: "1.1vh",
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {name}
    </Box>
  );
};

/* Real medals for the podium. The stage's `gold` token is platinum on purpose
   (the graphite scene carries no warm gold in its frames) — but the top three
   rows are exactly where gold / silver / bronze should read, so the badges get
   their own metallic palette instead of the stage token. */
const MEDALS = {
  1: { bg: "linear-gradient(180deg,#F2DC93 0%,#D9B85B 55%,#B8933F 100%)", ink: "#2E2408", line: "#D9B85B" },
  2: { bg: "linear-gradient(180deg,#EDF2F8 0%,#C7D0DC 55%,#A3AEBD 100%)", ink: "#1D232C", line: "#C7D0DC" },
  3: { bg: "linear-gradient(180deg,#E0A877 0%,#C08351 55%,#9C6438 100%)", ink: "#2C1B0C", line: "#C08351" },
};

const RankBadge = ({ rank, dark }) => {
  const t = tokens(dark);
  const isMedal = rank <= 3;
  const medal = MEDALS[rank];
  const fallbackBg = dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)";
  return (
    <Box
      sx={{
        // 15% smaller than the previous pass so adjacent rank badges
        // (1 / 2 / 3 rows) don't crowd each other vertically.
        width: "3.55vh",
        height: "3.55vh",
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: isMedal ? medal.bg : fallbackBg,
        color: isMedal ? medal.ink : t.textMuted,
        fontFamily: FONT,
        fontWeight: isMedal ? 700 : 500,
        fontSize: "1.7vh",
        fontFeatureSettings: '"tnum" 1',
        letterSpacing: "-0.02em",
        boxShadow: isMedal
          ? "inset 0 1px 0 rgba(255,255,255,0.55), 0 2px 6px rgba(0,0,0,0.35)"
          : "none",
      }}
    >
      {rank}
    </Box>
  );
};

/* ---------- Top bar ---------- */
const TopBar = ({ dark, setDark, period, setPeriod, paused, onMenu, menuOpen, onHome }) => {
  const t = tokens(dark);
  const brandHi = `0 0 0 3px ${t.accent}33, 0 6px 18px ${t.accent}33`;
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "7vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "2.2vw",
        zIndex: 10,
        background: dark
          ? "linear-gradient(180deg, rgba(11,19,32,0.55) 0%, rgba(11,19,32,0) 100%)"
          : "linear-gradient(180deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0) 100%)",
        backdropFilter: "blur(10px)",
      }}
    >
      {/* Left: brand goes home (same exit as Esc); hamburger opens workspace switcher. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0.7vw",
          pl: "0.7vw",
          pr: "0.6vw",
          height: "5vh",
          borderRadius: "14px",
          background: menuOpen
            ? ((dark && "rgba(86,132,214,0.14)") || "rgba(86,132,214,0.10)")
            : t.surfaceStrong,
          border: `1px solid ${menuOpen ? t.accent : t.border}`,
          boxShadow: menuOpen ? brandHi : "none",
          transition: "box-shadow 200ms ease, border-color 200ms ease, background 200ms ease",
        }}
      >
        <Box
          onClick={onHome}
          role="button"
          tabIndex={0}
          aria-label="Back to home"
          data-navfocus="top-0"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onHome?.();
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.7vw",
            cursor: "pointer",
            borderRadius: "10px",
            outline: "none",
            "&:hover, &:focus-visible": {
              "& .tv-brand-mark": {
                boxShadow: brandHi,
              },
            },
          }}
        >
          <Box
            className="tv-brand-mark"
            sx={{
              width: "3.6vh",
              height: "3.6vh",
              borderRadius: "10px",
              background: dark ? "#FFFFFF" : "#0B1320",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "box-shadow 200ms ease",
            }}
          >
            <img src={GMBLogo} alt="GMB" style={{ width: "70%", aspectRatio: "1/1" }} />
          </Box>
          <Display sx={{ fontSize: "2.1vh", color: t.text, letterSpacing: "-0.01em" }}>
            Game My Biz
          </Display>
        </Box>
        <Box sx={{ width: "1px", height: "2.6vh", background: t.border, mx: "0.35vw" }} />
        <Box
          onClick={onMenu}
          role="button"
          tabIndex={0}
          aria-label="Open team menu"
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onMenu?.();
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: menuOpen ? t.accent : t.textMuted,
            cursor: "pointer",
            borderRadius: "8px",
            px: "0.25vw",
            height: "3.2vh",
            outline: "none",
            "&:hover, &:focus-visible": { color: t.accent, background: dark ? "rgba(86,132,214,0.12)" : "rgba(86,132,214,0.10)" },
          }}
        >
          <svg width="2.4vh" height="2.4vh" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ width: "2.4vh", height: "2.4vh" }}>
            <path d="M4 7h16M4 12h16M4 17h16" />
          </svg>
        </Box>
      </Box>

      {/* Right: period tabs + pause + theme */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.7vw" }}>
        {/* Segmented period tabs — Today / This Week / This Month / Last Month */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            background: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: "12px",
            p: "0.3vh",
            height: "4.4vh",
            gap: "0.15vw",
          }}
        >
          {PERIODS.map((p, pIdx) => {
            const active = p === period;
            let activeBg = "transparent";
            if (active) activeBg = dark ? "rgba(86,132,214,0.18)" : "rgba(86,132,214,0.12)";
            return (
              <Box
                key={p}
                data-navfocus={`top-flt-${pIdx}`}
                onClick={setPeriod ? () => setPeriod(p) : undefined}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.35vw",
                  px: "0.85vw",
                  height: "100%",
                  borderRadius: "8px",
                  cursor: setPeriod ? "pointer" : "default",
                  userSelect: "none",
                  background: activeBg,
                  border: active ? `1px solid ${t.accent}66` : "1px solid transparent",
                  transition: "background 180ms ease, border-color 180ms ease",
                  "&:hover": setPeriod && !active
                    ? { background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)" }
                    : undefined,
                }}
              >
                {active && (
                  <Box
                    sx={{
                      width: "0.6vh",
                      height: "0.6vh",
                      borderRadius: "50%",
                      background: t.positive,
                      boxShadow: `0 0 0 3px ${dark ? "rgba(60,198,138,0.18)" : "rgba(60,198,138,0.18)"}`,
                    }}
                  />
                )}
                <Box
                  component="span"
                  sx={{
                    fontFamily: FONT,
                    fontWeight: active ? 700 : 500,
                    fontSize: "1.05vh",
                    letterSpacing: "0.04em",
                    color: active ? t.text : t.textMuted,
                    whiteSpace: "nowrap",
                  }}
                >
                  {PERIOD_LABELS[p]}
                </Box>
              </Box>
            );
          })}
        </Box>
        {paused && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "0.4vw",
              height: "4.2vh",
              px: "1vw",
              borderRadius: "999px",
              background: dark ? "rgba(240,179,61,0.14)" : "rgba(240,179,61,0.18)",
              color: t.gold,
            }}
          >
            <PauseIcon sx={{ fontSize: "1.8vh" }} />
            <Label sx={{ fontSize: "1.05vh" }}>Paused</Label>
          </Box>
        )}
        {/* Theme toggle — back at the top-right where it belongs */}
        <Box
          onClick={() => setDark((p) => !p)}
          role="button"
          aria-label="Toggle theme"
          data-navfocus="top-theme"
          sx={{
            width: "4.2vh",
            height: "4.2vh",
            borderRadius: "999px",
            background: t.surface,
            border: `1px solid ${t.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            color: t.text,
            transition: "border-color 160ms ease",
            "&:hover": { borderColor: t.accent },
          }}
        >
          {dark ? <LightModeIcon sx={{ fontSize: "2.1vh" }} /> : <DarkModeIcon sx={{ fontSize: "2.1vh" }} />}
        </Box>
      </Box>
    </Box>
  );
};


/* ---------- Scene indicator ---------- */
/* Scene indicator — "B / Slim": one slim segment per scene + a small label.
   Completed segments read full accent, the active one fills left→right over the
   scene duration, upcoming ones sit on a faint track. Cleaner than the glossy
   striped capsule; the label names what's on screen. */
const SceneIndicator = ({ index, total, dark, paused, label }) => {
  const accent = dark ? "#5684d6" : "#3A5FC0";
  const track = dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.12)";
  const labelColor = dark ? "rgba(236,237,237,0.6)" : "rgba(11,19,32,0.55)";
  return (
    <Box
      sx={{
        position: "absolute",
        bottom: "2.2vh",
        left: 0,
        right: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "1vw",
        zIndex: 10,
      }}
    >
      <Box sx={{ display: "flex", gap: "0.5vw" }}>
        {Array.from({ length: total }).map((_, i) => {
          const done = i < index;
          const active = i === index;
          return (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={`scene-seg-${i}`}
              sx={{
                position: "relative",
                width: "3vw",
                height: "0.5vh",
                minHeight: "3px",
                borderRadius: "999px",
                background: track,
                overflow: "hidden",
              }}
            >
              {done && (
                <Box sx={{ position: "absolute", inset: 0, background: accent, borderRadius: "999px" }} />
              )}
              {active && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    transformOrigin: "left center",
                    background: accent,
                    borderRadius: "999px",
                    animation: paused ? "none" : `tvSceneSeg ${SCENE_DURATION_MS}ms linear forwards`,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>

      {label && (
        <Box
          sx={{
            fontFamily: MONO_FONT,
            fontWeight: 500,
            fontSize: "1.05vh",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: labelColor,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Box>
      )}

      <style>{`@keyframes tvSceneSeg { from { transform: scaleX(0); } to { transform: scaleX(1); } }`}</style>
    </Box>
  );
};

/* ---------- View tabs — switch between the dashboards combined on ONE TV ----------
   Shown only when a workspace has 2+ views on its TV (its assigned dashboards,
   plus the built-in board). One connected segmented control, bottom-centre; the
   active view is the filled tab. Click a tab, or press ◀ / ▶ on the remote. */
const ViewTabs = ({ tabs, activeId, dark, onSelect }) => {
  const track = dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)";
  const hair = dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)";
  const ink = dark ? "#ECEDED" : "#0B1320";
  const muted = dark ? "rgba(236,237,237,0.6)" : "rgba(11,19,32,0.55)";
  return (
    <Box
      sx={{
        position: "absolute",
        bottom: "2.1vh",
        left: 0,
        right: 0,
        display: "flex",
        justifyContent: "center",
        px: "2vw",
        zIndex: 20,
        pointerEvents: "none",
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "stretch",
          gap: "0.3vw",
          maxWidth: "100%",
          background: track,
          border: `1px solid ${hair}`,
          borderRadius: "13px",
          padding: "0.45vh",
          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.4)",
          pointerEvents: "auto",
          overflow: "hidden",
        }}
      >
        {tabs.map((tb) => {
          const on = tb.id === activeId;
          return (
            <Box
              key={tb.id}
              component="button"
              type="button"
              data-navfocus={`tab-${tb.id}`}
              onClick={() => onSelect(tb.id)}
              title={tb.label}
              sx={{
                border: 0,
                cursor: "pointer",
                fontFamily: FONT,
                fontWeight: on ? 600 : 500,
                fontSize: "1.5vh",
                letterSpacing: "-0.005em",
                padding: "0.95vh 1.7vh",
                borderRadius: "10px",
                whiteSpace: "nowrap",
                maxWidth: "24vw",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: on ? "#FFFFFF" : muted,
                background: on ? "linear-gradient(180deg,#5f8fe0,#4a79cd)" : "transparent",
                boxShadow: on
                  ? "0 2px 8px rgba(86,132,214,0.42), inset 0 1px 0 rgba(255,255,255,0.22)"
                  : "none",
                transition: "background 200ms ease, color 200ms ease, box-shadow 200ms ease",
                "&:hover": on ? {} : { background: "rgba(255,255,255,0.07)", color: ink },
                "&:focus-visible": { outline: "2px solid #5684d6", outlineOffset: "2px" },
              }}
            >
              {tb.label}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ---------- HUD Frame (sci-fi card with beveled corners, glow bar, attached title tab) ---------- */
const HUD_BEVEL = 14; // px — diagonal corner cut

// Asymmetric: top-left + bottom-right cut, other corners square. Same on every card so the system reads consistent.
const HUD_CLIP = `polygon(
  ${HUD_BEVEL}px 0,
  100% 0,
  100% calc(100% - ${HUD_BEVEL}px),
  calc(100% - ${HUD_BEVEL}px) 100%,
  0 100%,
  0 ${HUD_BEVEL}px
)`;

const HudFrame = ({ accent, dark, children, contentSx, sx }) => {
  // Measure the wrapper so we can draw the beveled border with PIXEL-EXACT
  // coordinates on top of the children. Children may have an opaque background
  // (e.g. GoalMountain) which would otherwise cover the underlying stroke
  // layer and hide the border ring entirely.
  const wrapperRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!wrapperRef.current) return undefined;
    const el = wrapperRef.current;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setDims({ w: rect.width, h: rect.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const t = tokens(dark);
  const accentColor = accent || t.accent;
  const stroke = dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.22)";
  const innerHi = dark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.5)";
  const fill = dark
    ? "linear-gradient(180deg, rgba(38,39,42,0.88) 0%, rgba(28,29,32,0.92) 100%)"
    : "linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(244,247,252,0.85) 100%)";

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: "relative",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      {/* Border ring — pixel-exact SVG overlay sitting ABOVE children (z=2)
          so the beveled border is visible even when a child component fills
          the area with an opaque background. Coords come from ResizeObserver. */}
      {dims.w > 0 && dims.h > 0 && (
        <svg
          width={dims.w}
          height={dims.h}
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            pointerEvents: "none",
            overflow: "visible",
          }}
        >
          <path
            d={`M ${HUD_BEVEL} 0.5 L ${dims.w - 0.5} 0.5 L ${dims.w - 0.5} ${dims.h - HUD_BEVEL} L ${dims.w - HUD_BEVEL} ${dims.h - 0.5} L 0.5 ${dims.h - 0.5} L 0.5 ${HUD_BEVEL} Z`}
            fill="none"
            stroke={stroke}
            strokeWidth="1"
          />
        </svg>
      )}
      {/* Stroke layer — outer slightly bigger, then fill is inset by 1px = creates a 1px border that follows the bevel */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          clipPath: HUD_CLIP,
          background: stroke,
          pointerEvents: "none",
        }}
      />
      {/* Fill layer */}
      <Box
        sx={{
          position: "absolute",
          inset: "1px",
          clipPath: HUD_CLIP,
          background: fill,
          pointerEvents: "none",
        }}
      />
      {/* Inner top highlight — thin sheen line just inside the top edge */}
      <Box
        sx={{
          position: "absolute",
          top: "1px",
          left: `${HUD_BEVEL + 4}px`,
          right: "8px",
          height: "1px",
          background: `linear-gradient(90deg, transparent 0%, ${innerHi} 30%, ${innerHi} 70%, transparent 100%)`,
          pointerEvents: "none",
          zIndex: 2,
        }}
      />
      {/* Top edge glow bar — soft horizontal sweep, gentle pulse */}
      <Box
        sx={{
          position: "absolute",
          top: "2px",
          left: "20%",
          right: "20%",
          height: "10px",
          background: `radial-gradient(ellipse at center, ${accentColor}55 0%, transparent 70%)`,
          filter: "blur(6px)",
          pointerEvents: "none",
          animation: "tvHudPulse 4s ease-in-out infinite",
          zIndex: 2,
        }}
      />
      {/* Corner accent ticks — top-left cut + bottom-right cut, accent color */}
      <Box
        sx={{
          position: "absolute",
          top: "-1px",
          left: "-1px",
          width: `${HUD_BEVEL + 6}px`,
          height: `${HUD_BEVEL + 6}px`,
          pointerEvents: "none",
          zIndex: 2,
        }}
      >
        <svg viewBox="0 0 24 24" style={{ width: "100%", height: "100%", display: "block" }}>
          <path
            d={`M 0 ${HUD_BEVEL + 4} L 0 ${HUD_BEVEL} L ${HUD_BEVEL} 0 L ${HUD_BEVEL + 4} 0`}
            fill="none"
            stroke={accentColor}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </Box>
      <Box
        sx={{
          position: "absolute",
          bottom: "-1px",
          right: "-1px",
          width: `${HUD_BEVEL + 6}px`,
          height: `${HUD_BEVEL + 6}px`,
          pointerEvents: "none",
          zIndex: 2,
        }}
      >
        <svg viewBox="0 0 24 24" style={{ width: "100%", height: "100%", display: "block" }}>
          <path
            d={`M ${HUD_BEVEL + 4} 24 L 24 24 L 24 ${HUD_BEVEL + 4} L ${24 - HUD_BEVEL - 4} ${24}`}
            fill="none"
            stroke={accentColor}
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </Box>
      {/* Status dot at top-right (small luminous indicator) */}
      <Box
        sx={{
          position: "absolute",
          top: "1.4vh",
          right: "1vw",
          width: "0.7vh",
          height: "0.7vh",
          borderRadius: "50%",
          background: t.positive,
          boxShadow: `0 0 8px ${t.positive}, 0 0 0 2px ${dark ? "rgba(11,19,32,0.6)" : "rgba(255,255,255,0.6)"}`,
          animation: "tvHudBlink 2.4s ease-in-out infinite",
          pointerEvents: "none",
          zIndex: 2,
        }}
      />

      {/* Children */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          p: "1.6vh 1.4vw",
          ...contentSx,
        }}
      >
        {children}
      </Box>

      <style>{`
        @keyframes tvHudPulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 1; }
        }
        @keyframes tvHudBlink {
          0%, 60%, 100% { opacity: 1; }
          70%, 80% { opacity: 0.35; }
        }
      `}</style>
    </Box>
  );
};

/* ---------- Scene title + section header ---------- */
const SceneHeader = ({ index, total, title, icon, period, dark }) => {
  const t = tokens(dark);
  // Banner background — brushed graphite so it reads as part of the card system
  // (the old deep-navy strip stood out as a blue box on the graphite stage). The
  // blue lives in the accents (icon, live pill), not this bar.
  const bannerBg = dark
    ? "linear-gradient(180deg, #2A2B2F 0%, #1E1F22 100%)"
    : "linear-gradient(180deg, #FFFFFF 0%, #F4F6FB 100%)";

  return (
    <Box
      sx={{
        position: "relative",
        background: bannerBg,
        borderRadius: "12px",
        height: "4.4vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: "1.6vw",
        boxShadow: dark
          ? "0 8px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)"
          : "0 6px 18px rgba(15,23,42,0.18), inset 0 1px 0 rgba(255,255,255,0.12)",
        overflow: "hidden",
      }}
    >
      {/* Subtle highlight stripe along the top edge */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)",
          pointerEvents: "none",
        }}
      />
      {/* Center: title with icon */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.7vw",
          pointerEvents: "none",
        }}
      >
        {icon && (
          <Box
            sx={{
              fontSize: "1.8vh",
              lineHeight: 1,
              color: "#FFFFFF",
              opacity: 0.9,
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
            }}
          >
            {icon}
          </Box>
        )}
        <Box
          component="span"
          sx={{
            fontFamily: MONO_FONT,
            fontWeight: 500,
            fontSize: "1.8vh",
            color: "#FFFFFF",
            letterSpacing: "-0.01em",
            textShadow: "0 1px 2px rgba(0,0,0,0.3)",
          }}
        >
          {title}
        </Box>
      </Box>

      {/* Right: period chip */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.4vw",
          px: "0.8vw",
          py: "0.5vh",
          borderRadius: "999px",
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.14)",
          minWidth: "5vw",
          justifyContent: "center",
        }}
      >
        <Box sx={{ width: "0.7vh", height: "0.7vh", borderRadius: "50%", background: t.positive, boxShadow: `0 0 8px ${t.positive}` }} />
        <Box
          component="span"
          sx={{
            fontFamily: FONT,
            fontWeight: 500,
            fontSize: "1.05vh",
            letterSpacing: "0.16em",
            color: "rgba(255,255,255,0.9)",
            textTransform: "uppercase",
          }}
        >
          {PERIOD_LABELS[period]} · Live
        </Box>
      </Box>
    </Box>
  );
};

const SectionHeader = ({ icon, title, hint, dark, accent }) => {
  const t = tokens(dark);
  const accentColor = accent || t.accent;
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: "1vh" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "0.7vw" }}>
        <Box
          sx={{
            width: "3.4vh",
            height: "3.4vh",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: dark ? `${accentColor}22` : `${accentColor}1A`,
            color: accentColor,
            fontSize: "1.8vh",
            fontWeight: 500,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.2vh" }}>
          <Text
            weight={500}
            sx={{
              fontFamily: MONO_FONT,
              fontSize: "1.75vh",
              color: t.text,
              letterSpacing: "-0.005em",
            }}
          >
            {title}
          </Text>
          {hint && <Label sx={{ color: t.textFaint, fontSize: "0.95vh" }}>{hint}</Label>}
        </Box>
      </Box>
    </Box>
  );
};

/* ---------- Scene 1: Leaderboard ---------- */
// Grid template shared by header + rows so columns line up perfectly.
// Columns: rank · avatar · name · office · K1 · K2 · K3 · K4 · K5 · points
// KPI columns use minmax(0, 1fr) so headers can wrap on narrower viewports.
const LEADERBOARD_GRID =
  "minmax(2.6vw, 3.4vw) minmax(2.8vw, 3.6vw) minmax(6vw, 1fr) minmax(4.5vw, 7vw) repeat(5, minmax(0, 1fr)) minmax(4.5vw, 7vw)";

/** Header label styles — wrap inside the cell instead of overlapping neighbors. */
const leaderboardHdrLabelSx = (t, textAlign = "left") => ({
  color: t.textMuted,
  fontSize: "clamp(0.62vh, 0.78vw, 0.95vh)",
  letterSpacing: "0.07em",
  lineHeight: 1.2,
  whiteSpace: "normal",
  overflowWrap: "break-word",
  hyphens: "auto",
  minWidth: 0,
  textAlign,
});

const kpiMetricTitle = (kpi) => kpi?.title || kpi?.name || "Metric";

const kpiGuideText = (kpi) => {
  const raw = kpi?.guideText;
  return typeof raw === "string" ? raw.trim() : "";
};

// Heuristic — KPIs whose name suggests money show with a $ prefix.
const isMonetaryKpi = (kpi) =>
  /(revenue|sale|sell|payment|amount|dollar|\$|cash)/i.test(kpiMetricTitle(kpi));

/** Same wording as Game Rules modal — points earned per metric amount. */
const kpiGamificationRuleText = (kpi) => {
  if (!isKpiGamified(kpi)) return "";

  const pointsRule = kpi.pointsRule || kpi.model?.pointsRule;
  let metricAmount;
  if (typeof kpi.pointScale === "number") metricAmount = kpi.pointScale;
  else if (pointsRule && typeof pointsRule.metricAmount === "number") metricAmount = pointsRule.metricAmount;
  else if (typeof kpi.optional?.unit === "number") metricAmount = kpi.optional.unit;
  else metricAmount = kpi.model?.defaultPointScale ?? 1;

  let pointsEarned;
  if (typeof kpi.pointValue === "number") pointsEarned = kpi.pointValue;
  else if (pointsRule && typeof pointsRule.pointsEarned === "number") pointsEarned = pointsRule.pointsEarned;
  else if (typeof kpi.optional?.point === "number") pointsEarned = kpi.optional.point;
  else pointsEarned = kpi.model?.defaultPointValue ?? 10;

  const unitLabel = kpi.kind === "$" || isMonetaryKpi(kpi) ? "$" : "units";
  const amtDisplay = formatNumber(metricAmount);
  const ptsDisplay = formatNumber(pointsEarned);

  return `Every ${amtDisplay} ${unitLabel} earns ${ptsDisplay} pts`;
};

/** Guide text when set; otherwise gamification rule for live metrics. */
const kpiMetricSubtext = (kpi) => {
  const guide = kpiGuideText(kpi);
  if (guide) return guide;
  return kpiGamificationRuleText(kpi);
};

/** Admin-set daily goal (Gamification rules modal) — teal pill under the metric name. */
const kpiDailyGoalText = (kpi) => {
  const raw = kpi?.dailyGoalText;
  return typeof raw === "string" ? raw.trim() : "";
};

/** Shared title row height so KPI headers line up with Rank / Technician / etc. */
const KPI_HDR_TITLE_MIN = "2.35vh";

const flexAlignFromTextAlign = (textAlign = "left") => {
  if (textAlign === "right") return "flex-end";
  if (textAlign === "center") return "center";
  return "flex-start";
};

const hdrTitleRowSx = (textAlign = "left") => ({
  minHeight: KPI_HDR_TITLE_MIN,
  width: "100%",
  minWidth: 0,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: flexAlignFromTextAlign(textAlign),
});

/** Non-KPI column header — same title band as metric labels. */
const HdrLabelCell = ({ children, dark, textAlign = "left", sx: labelSxExtra }) => {
  const t = tokens(dark);
  return (
    <Box sx={hdrTitleRowSx(textAlign)}>
      <Label sx={{ ...leaderboardHdrLabelSx(t, textAlign), ...labelSxExtra }}>{children}</Label>
    </Box>
  );
};

const kpiGuidePillSx = (dark, textAlign, isHeader) => ({
  fontFamily: FONT,
  fontWeight: 500,
  fontSize: isHeader ? "clamp(0.55vh, 0.682vw, 0.814vh)" : "clamp(0.638vh, 0.77vw, 0.902vh)",
  lineHeight: 1,
  margin: 0,
  color: dark ? "rgba(248,250,252,0.95)" : "#334155",
  textAlign: "center",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  height: isHeader ? "2em" : "2.05em",
  px: "0.55em",
  py: 0,
  maxWidth: "100%",
  overflow: "hidden",
  background: dark ? "rgba(8,12,20,0.55)" : "rgba(15,23,42,0.06)",
  border: dark ? "1px solid rgba(203,213,225,0.38)" : "1px solid rgba(15,23,42,0.14)",
  borderRadius: "5px",
  boxShadow: "none",
  "& .kpi-guide-pill-text": {
    display: "block",
    lineHeight: 1,
    transform: "translateY(0.06em)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
});

const kpiDailyGoalPillSx = (dark, isHeader) => ({
  fontFamily: FONT,
  fontWeight: 500,
  fontSize: isHeader ? "clamp(0.64vh, 0.8vw, 0.95vh)" : "clamp(0.74vh, 0.9vw, 1.05vh)",
  lineHeight: 1.15,
  margin: 0,
  color: dark ? "rgba(94,234,212,0.95)" : "#0d9488",
  textAlign: "center",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  height: "auto",
  minHeight: 0,
  px: "1em",
  py: "0.42em",
  maxWidth: "100%",
  overflow: "hidden",
  background: dark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
  border: dark ? "1px solid rgba(94,234,212,0.45)" : "1px solid rgba(20,184,166,0.4)",
  borderRadius: "9999px",
  boxShadow: "none",
  "& .kpi-goal-pill-text": {
    display: "block",
    lineHeight: 1.15,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
});

/** Metric name with optional guide text beneath (TV-readable, compact). */
const KpiMetricLabel = ({ kpi, dark, textAlign = "center", variant = "header", showGuideText = true }) => {
  const t = tokens(dark);
  const title = kpiMetricTitle(kpi);
  const subtext = showGuideText ? kpiMetricSubtext(kpi) : "";
  const dailyGoal = kpiDailyGoalText(kpi);
  const isHeader = variant === "header";

  if (isHeader) {
    const pillAlign = flexAlignFromTextAlign(textAlign);
    return (
      <Box
        sx={{
          minWidth: 0,
          maxWidth: "100%",
          width: "100%",
          alignSelf: "start",
          display: "flex",
          flexDirection: "column",
          alignItems: pillAlign,
        }}
      >
        <Box
          sx={{
            minHeight: KPI_HDR_TITLE_MIN,
            display: "flex",
            flexDirection: "column",
            alignItems: pillAlign,
            justifyContent: "flex-start",
            gap: (subtext || dailyGoal) ? "0.28vh" : 0,
            minWidth: 0,
            maxWidth: "100%",
            width: "100%",
          }}
        >
          <Label
            sx={{
              ...leaderboardHdrLabelSx(t, textAlign),
              width: "100%",
              lineHeight: 1.1,
              mb: 0,
              pb: 0,
            }}
          >
            {title}
          </Label>
          {subtext ? (
            <Box
              component="span"
              title={subtext}
              sx={{
                ...kpiGuidePillSx(dark, textAlign, true),
                width: "max-content",
                maxWidth: "100%",
              }}
            >
              <Box component="span" className="kpi-guide-pill-text">
                {subtext}
              </Box>
            </Box>
          ) : null}
          {dailyGoal ? (
            <Box
              component="span"
              title={`Daily Goal: ${dailyGoal}`}
              sx={{
                ...kpiDailyGoalPillSx(dark, true),
                width: "max-content",
                maxWidth: "100%",
              }}
            >
              <Box component="span" className="kpi-goal-pill-text">
                Daily Goal: {dailyGoal}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minWidth: 0,
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: (subtext || dailyGoal) ? "1px" : 0,
      }}
    >
      <Label
        sx={{
          color: t.textMuted,
          fontSize: "1vh",
          letterSpacing: "0.12em",
          lineHeight: 1.1,
          textAlign: "left",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
          display: "block",
        }}
      >
        {title}
      </Label>
      {subtext ? (
        <Box
          component="span"
          title={subtext}
          sx={{ ...kpiGuidePillSx(dark, "left", false), width: "max-content", maxWidth: "100%" }}
        >
          <Box component="span" className="kpi-guide-pill-text">
            {subtext}
          </Box>
        </Box>
      ) : null}
      {dailyGoal ? (
        <Box
          component="span"
          title={`Daily Goal: ${dailyGoal}`}
          sx={{ ...kpiDailyGoalPillSx(dark, false), width: "max-content", maxWidth: "100%" }}
        >
          <Box component="span" className="kpi-goal-pill-text">
            Daily Goal: {dailyGoal}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

const formatKpiCell = (kpi, score) => {
  const amt = Number(score?.amt) || 0;
  // Percentage KPIs store amt as a decimal fraction (0.46 = 46%). Rounding to
  // an integer collapses it to 0/1, so format the raw fraction as a percent —
  // same as /home's LiveLeaderboard, which passes kpi.kind to formatNumber.
  if (kpi?.kind === "%") {
    return amt ? formatKpiPercent(kpi, amt) : "0";
  }
  const value = Math.round(amt);
  if (!value) return "0";
  return isMonetaryKpi(kpi) ? `$${formatNumber(value)}` : formatNumber(value);
};

const OfficeBadge = ({ office, dark }) => {
  if (!office) return null;
  // Yeti gets a white chip — same treatment as the rest of the Yeti row, so
  // the "The Yeti" label reads as a target/benchmark rather than a team color.
  const isYetiBadge = office === YETI_TEAM;
  const accent = teamColorFor(office);
  let bg;
  let fg;
  let borderColor;
  if (isYetiBadge) {
    bg = dark ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.85)";
    fg = dark ? "#FFFFFF" : "#0B1320";
    borderColor = dark ? "rgba(255,255,255,0.35)" : "rgba(15,23,42,0.18)";
  } else {
    bg = dark ? `${accent}26` : `${accent}1A`;
    fg = dark ? `${accent}DD` : accent;
    borderColor = `${accent}33`;
  }
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: "2.4vh",
        px: "0.8vw",
        borderRadius: "999px",
        background: bg,
        color: fg,
        fontFamily: FONT,
        fontWeight: 500,
        fontSize: "1.05vh",
        letterSpacing: "0.16em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        border: `1px solid ${borderColor}`,
      }}
    >
      {office}
    </Box>
  );
};

const LeaderRow = ({ player, rank, dark, kpis, isFirst, yetiBucket }) => {
  const t = tokens(dark);
  const total = player?.scores?.total || 0;
  const fullName =
    player?.firstName || player?.lastName
      ? `${player?.firstName || ""} ${player?.lastName || ""}`.trim()
      : player?.email || "—";
  const office = teamForPlayer(player);
  // Yeti is a benchmark, not a real player — paint the entire row in a
  // visible grey wash so it reads as "the target line" at a glance.
  const isYetiRow = !!player?.isYeti;
  const yetiRowBg = dark
    ? "rgba(148,163,184,0.18)"  // slate-400 @ ~18% on dark
    : "rgba(100,116,139,0.16)"; // slate-500 @ ~16% on light
  // Podium rows carry their medal: a colour rail down the left edge, a faint
  // wash trailing off it, and a matching ring around the avatar.
  const medal = rank <= 3 ? MEDALS[rank] : null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: LEADERBOARD_GRID,
        alignItems: "center",
        gap: "0.6vw",
        px: "1.2vw",
        // Rows share the container's height so a long roster still fits without
        // clumping or hugging a tiny height — but capped, so a short roster (or
        // a filtered-down one) doesn't stretch a couple of rows across the whole
        // board. Spare room is left empty below the last row.
        flex: 1,
        minHeight: "3.2vh",
        maxHeight: "6.4vh",
        borderTop: isFirst ? "none" : `1px solid ${t.hairline}`,
        position: "relative",
        ...(medal && {
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: "8%",
            bottom: "8%",
            width: "3px",
            borderRadius: "0 3px 3px 0",
            background: medal.line,
          },
        }),
        ...(medal && !isYetiRow && {
          background: `linear-gradient(90deg, ${medal.line}1f 0%, transparent 40%)`,
        }),
        ...(isYetiRow && { background: yetiRowBg }),
      }}
    >
      <RankBadge rank={rank} dark={dark} />
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            display: "flex",
            borderRadius: "50%",
            ...(medal && { boxShadow: `0 0 0 2px ${medal.line}, 0 0 12px ${medal.line}55` }),
          }}
        >
          <SoftProfile size="sm" src={player?.image} name={fullName} />
        </Box>
      </Box>
      <Text
        weight={player?.ownId ? 700 : 500}
        sx={{
          fontSize: "1.4vh",
          color: t.text,
          letterSpacing: "-0.005em",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {fullName}
      </Text>
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        <OfficeBadge office={office} dark={dark} />
      </Box>
      {(kpis || []).slice(0, 5).map((kpi) => {
        const score = player?.scores?.[kpi._id];
        const hasValue = (score?.amt || 0) > 0;
        const playerAmt = score?.amt || 0;
        const yetiAmt = yetiBucket?.[kpi._id]?.amt || 0;
        const yetiDiff = playerAmt - yetiAmt;
        const showChip = yetiAmt > 0 || playerAmt > 0;
        const positive = yetiDiff > 0;
        const negative = yetiDiff < 0;
        const chipColor = negative ? t.negative : t.positive;
        let chipBg;
        if (negative) {
          chipBg = dark ? "rgba(225,108,108,0.16)" : "rgba(225,108,108,0.12)";
        } else {
          chipBg = dark ? "rgba(60,198,138,0.16)" : "rgba(60,198,138,0.12)";
        }
        let arrow = "•";
        if (positive) arrow = "▲";
        else if (negative) arrow = "▼";
        let chipValue;
        if (kpi?.kind === "%") {
          // Percentage diff is a decimal fraction — format as percent, not int.
          chipValue = formatKpiPercent(kpi, Math.abs(yetiDiff));
        } else if (isMonetaryKpi(kpi)) {
          chipValue = `$${formatNumber(Math.round(Math.abs(yetiDiff)))}`;
        } else {
          chipValue = formatNumber(Math.round(Math.abs(yetiDiff)));
        }
        return (
          <Box
            key={kpi._id}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.25vh",
              textAlign: "center",
            }}
          >
            <Text
              weight={500}
              sx={{
                fontFamily: FONT,
                fontSize: "1.55vh",
                color: hasValue ? t.text : t.textFaint,
                fontFeatureSettings: '"tnum" 1, "lnum" 1',
                letterSpacing: "-0.005em",
                lineHeight: 1.1,
                textAlign: "center",
              }}
            >
              {formatKpiCell(kpi, score)}
            </Text>
            {showChip && (
              <Box
                component="span"
                sx={{
                  fontFamily: FONT,
                  color: chipColor,
                  background: chipBg,
                  borderRadius: "0.5vh",
                  padding: "0.15vh 0.55vh",
                  fontSize: "1vh",
                  fontWeight: 500,
                  fontFeatureSettings: '"tnum" 1, "lnum" 1',
                  letterSpacing: "-0.005em",
                  lineHeight: 1.1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.2vw",
                }}
              >
                <Box component="span" sx={{ fontSize: "0.85em", lineHeight: 1, transform: "translateY(-0.05em)" }}>{arrow}</Box>
                {chipValue}
              </Box>
            )}
          </Box>
        );
      })}
      {/* Pad missing KPIs if fewer than 5 */}
      {Array.from({ length: Math.max(0, 5 - (kpis?.length || 0)) }).map((_, idx) => (
        // eslint-disable-next-line react/no-array-index-key
        <Box key={`pad-${idx}`} />
      ))}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "baseline",
          gap: "0.25vw",
          width: "100%",
        }}
      >
        <Display sx={{ fontSize: "2.2vh", color: t.text }}>
          {formatNumber(Math.round(total))}
        </Display>
        <Label sx={{ fontSize: "0.85vh", color: t.textFaint }}>pts</Label>
      </Box>
    </Box>
  );
};

const WorkspaceScoreCard = ({ entry, accent, dark }) => {
  const t = tokens(dark);
  return (
    <Box
      sx={{
        flex: 1,
        background: t.surface,
        border: `1px solid ${t.border}`,
        borderRadius: "16px",
        px: "1.4vw",
        py: "1.6vh",
        display: "flex",
        flexDirection: "column",
        gap: "0.8vh",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: accent,
        }}
      />
      <Label sx={{ color: t.textMuted, fontSize: "1.05vh" }}>Workspace</Label>
      <Text weight={500} sx={{ fontSize: "2.2vh", color: t.text, letterSpacing: "-0.01em" }}>
        {entry.name}
      </Text>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.6vw", mt: "0.4vh" }}>
        <Display sx={{ fontSize: "3.6vh", color: t.text }}>
          {formatNumber(Math.round(entry.points))}
        </Display>
        <Label sx={{ color: t.textFaint, fontSize: "1.05vh" }}>points</Label>
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.4vw" }}>
        <Text weight={500} sx={{ fontSize: "1.6vh", color: t.textMuted }}>
          ${formatNumber(Math.round(entry.revenue))}
        </Text>
        <Label sx={{ color: t.textFaint, fontSize: "0.95vh" }}>avg revenue</Label>
      </Box>
    </Box>
  );
};

const WorkspaceSplitBar = ({ entries, dark }) => {
  const t = tokens(dark);
  const total = entries.reduce((s, e) => s + (e.points || 0), 0) || 1;
  const palette = [t.accent, t.gold, t.bronze, t.silver];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.9vh" }}>
      <Box
        sx={{
          display: "flex",
          height: "1.4vh",
          borderRadius: "999px",
          overflow: "hidden",
          background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        }}
      >
        {entries.map((e, i) => (
          <Box
            key={e.id}
            sx={{
              width: `${((e.points || 0) / total) * 100}%`,
              background: palette[i % palette.length],
              transition: "width 600ms ease",
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: "1vw" }}>
        {entries.map((e, i) => (
          <Box key={`leg-${e.id}`} sx={{ display: "flex", alignItems: "center", gap: "0.4vw" }}>
            <Box sx={{ width: "0.9vh", height: "0.9vh", borderRadius: "999px", background: palette[i % palette.length] }} />
            <Label sx={{ color: t.textMuted, fontSize: "1vh" }}>
              {e.name} · {Math.round(((e.points || 0) / total) * 100)}%
            </Label>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* ---------- Office illustrations (split: Kitsap far-left, Seattle far-right) -
   Both sides always clearly visible. Winning side gets a "WINNING" badge
   floating above the silhouette's peak (no edge glow — too noisy on a TV). */
const sideOpacity = (winning, dark) => {
  if (winning === true) return dark ? 0.85 : 0.9;
  if (winning === false) return dark ? 0.5 : 0.6;
  return dark ? 0.7 : 0.78; // tied / unknown
};

const WinningBadge = ({ accent, dark }) => (
  <Box
    sx={{
      position: "absolute",
      // Dead center of the silhouette container.
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.35vw",
      px: "0.9vw",
      py: "0.55vh",
      borderRadius: "999px",
      background: dark ? `${accent}33` : `${accent}1A`,
      border: `1px solid ${accent}`,
      color: dark ? "#fff" : accent,
      fontFamily: FONT,
      fontWeight: 600,
      fontSize: "1.15vh",
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      boxShadow: `0 0 0 3px ${accent}22, 0 4px 14px ${accent}66`,
      animation: "tvWinningPop 600ms ease-out",
      pointerEvents: "none",
      zIndex: 3,
    }}
  >
    <Box component="span" sx={{ color: accent, fontSize: "1.3vh", lineHeight: 1 }}>♛</Box>
    Winning
  </Box>
);

const SeattleIllustration = ({ accent, dark, winning }) => {
  const op = sideOpacity(winning, dark);
  const gradId = `sky-fg-${accent.replace("#", "")}`;
  const bgId = `sky-bg-${accent.replace("#", "")}`;
  const symId = `sky-sym-${accent.replace("#", "")}`;
  return (
    <Box
      sx={{
        position: "absolute",
        right: 0,
        bottom: 0,
        width: "19%",
        height: "55%",
        pointerEvents: "none",
        opacity: op,
        transition: "opacity 600ms ease",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "flex-end",
      }}
    >
      <svg
        viewBox="0 0 200 110"
        preserveAspectRatio="xMidYMax meet"
        style={{ width: "100%", height: "100%", display: "block" }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.95" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.55" />
          </linearGradient>
          <linearGradient id={bgId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.6" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.25" />
          </linearGradient>
          <symbol id={symId} viewBox="0 0 200 110">
            <rect x="20" y="80" width="14" height="30" />
            <rect x="35" y="74" width="10" height="36" />
            <rect x="46" y="82" width="14" height="28" />
            <rect x="62" y="76" width="10" height="34" />
            <rect x="74" y="80" width="12" height="30" />
            <rect x="115" y="78" width="12" height="32" />
            <rect x="129" y="74" width="10" height="36" />
            <rect x="141" y="82" width="14" height="28" />
            <rect x="157" y="78" width="10" height="32" />
            <rect x="169" y="80" width="12" height="30" />
            <rect x="14" y="68" width="10" height="42" />
            <rect x="26" y="72" width="14" height="38" />
            <rect x="42" y="62" width="11" height="48" />
            <rect x="55" y="74" width="9" height="36" />
            <rect x="66" y="68" width="14" height="42" />
            <rect x="120" y="70" width="13" height="40" />
            <rect x="135" y="62" width="10" height="48" />
            <rect x="147" y="68" width="14" height="42" />
            <rect x="163" y="72" width="9" height="38" />
            <rect x="174" y="68" width="14" height="42" />
            {/* Space Needle */}
            <rect x="99.4" y="2" width="1.2" height="14" />
            <path d="M 96 16 Q 100 12 104 16 Z" />
            <ellipse cx="100" cy="20" rx="13" ry="3.4" />
            <path d="M 88 20 L 95 27 L 105 27 L 112 20 Z" />
            <rect x="97.5" y="27" width="5" height="50" />
            <path d="M 97.5 77 L 90 110 L 110 110 L 102.5 77 Z" />
          </symbol>
        </defs>
        <g fill={`url(#${gradId})`}>
          <use href={`#${symId}`} />
        </g>
      </svg>
      {winning === true && <WinningBadge accent={accent} dark={dark} />}
    </Box>
  );
};

/* Kitsap = Bremerton naval shipyard. Aircraft carrier silhouette on water. */
const KitsapIllustration = ({ accent, dark, winning }) => {
  const op = sideOpacity(winning, dark);
  const gradId = `car-fg-${accent.replace("#", "")}`;
  const bgId = `car-bg-${accent.replace("#", "")}`;
  const symId = `car-sym-${accent.replace("#", "")}`;
  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        bottom: 0,
        width: "19%",
        height: "55%",
        pointerEvents: "none",
        opacity: op,
        transition: "opacity 600ms ease",
        display: "flex",
        justifyContent: "flex-start",
        alignItems: "flex-end",
      }}
    >
      <svg
        viewBox="0 0 200 110"
        preserveAspectRatio="xMidYMax meet"
        style={{ width: "100%", height: "100%", display: "block" }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.95" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.6" />
          </linearGradient>
          <linearGradient id={bgId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.55" />
            <stop offset="100%" stopColor={accent} stopOpacity="0.22" />
          </linearGradient>
          <symbol id={symId} viewBox="0 0 200 110">
            <path d="M 32 90 L 168 90 L 162 100 L 38 100 Z" />
            <path d="M 22 86 L 178 86 L 172 90 L 28 90 Z" />
            <path d="M 22 86 L 28 90 L 18 90 Z" />
            <rect x="112" y="74" width="16" height="12" />
            <rect x="119" y="62" width="2" height="12" />
            <rect x="116" y="64" width="8" height="0.8" />
            <rect x="117" y="68" width="6" height="0.8" />
            <rect x="119.7" y="58" width="0.6" height="4" />
            <path d="M 50 84 L 55 82 L 58 84 Z" />
            <path d="M 64 84 L 69 82 L 72 84 Z" />
            <path d="M 78 84 L 83 82 L 86 84 Z" />
            <path d="M 92 84 L 97 82 L 100 84 Z" />
            <path d="M 140 84 L 145 82 L 148 84 Z" />
            <path d="M 152 84 L 157 82 L 160 84 Z" />
          </symbol>
        </defs>

        <g fill={`url(#${bgId})`}>
          <path d="M 0 95 L 18 78 L 32 88 L 48 70 L 66 84 L 82 74 L 100 86 L 118 76 L 136 88 L 154 72 L 172 84 L 188 76 L 200 86 L 200 110 L 0 110 Z" />
        </g>

        <g fill={accent} fillOpacity="0.22">
          <rect x="0" y="92" width="200" height="0.6" />
          <rect x="0" y="98" width="200" height="0.4" />
          <rect x="0" y="104" width="200" height="0.4" />
        </g>

        <g fill={`url(#${gradId})`}>
          <use href={`#${symId}`} />
        </g>
        <rect x="113.5" y="76.5" width="13" height="1.6" fill={accent} fillOpacity="0.5" />
        <ellipse cx="120" cy="62" rx="2.2" ry="1.2" fill={accent} fillOpacity="0.8" />
        <rect x="32" y="87.4" width="40" height="0.4" fill={accent} fillOpacity="0.55" />
        <rect x="80" y="87.4" width="28" height="0.4" fill={accent} fillOpacity="0.55" />

        <g fill={accent} fillOpacity="0.14">
          <rect x="34" y="100" width="130" height="1.2" />
          <rect x="44" y="103" width="110" height="0.8" />
        </g>
      </svg>
      {winning === true && <WinningBadge accent={accent} dark={dark} />}
    </Box>
  );
};

/* ---------- Office Scoreboard (Kitsap vs Seattle, footer of Scene 1) ---------- */
const OfficeCard = ({ name, points, avgRevenue, accent, alignRight, dark }) => {
  const t = tokens(dark);
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: alignRight ? "flex-end" : "flex-start",
        gap: "0.25vh",
        textAlign: alignRight ? "right" : "left",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5vw",
          flexDirection: alignRight ? "row-reverse" : "row",
        }}
      >
        <Box
          sx={{
            width: "0.9vh",
            height: "0.9vh",
            borderRadius: "50%",
            background: accent,
            boxShadow: `0 0 6px ${accent}`,
          }}
        />
        {/* Team name now bigger, less tracked, more like a "title" */}
        <Box
          component="span"
          sx={{
            fontFamily: FONT,
            fontWeight: 500,
            fontSize: "1.9vh",
            letterSpacing: "0.04em",
            color: accent,
          }}
        >
          {name}
        </Box>
      </Box>
      {/* Points + label inline, smaller than the team name. Side-by-side row. */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: "0.5vw",
          flexDirection: alignRight ? "row-reverse" : "row",
        }}
      >
        <Display sx={{ fontSize: "1.8vh", color: t.text, letterSpacing: "-0.02em", lineHeight: 1 }}>
          {formatNumber(Math.round(points || 0))}
        </Display>
        <Label sx={{ color: t.textFaint, fontSize: "0.85vh" }}>points</Label>
      </Box>
      {/* Avg revenue line — also inline number + label */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "baseline",
          gap: "0.5vw",
          flexDirection: alignRight ? "row-reverse" : "row",
        }}
      >
        <Display sx={{ fontSize: "1.3vh", color: t.textMuted, letterSpacing: "-0.01em" }}>
          ${formatNumber(Math.round(avgRevenue || 0))}
        </Display>
        <Label sx={{ color: t.textFaint, fontSize: "0.75vh" }}>avg revenue</Label>
      </Box>
    </Box>
  );
};

const VsDivider = ({ dark }) => {
  const t = tokens(dark);
  return (
    <Box
      sx={{
        flexShrink: 0,
        width: "5vw",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.6vh",
      }}
    >
      <Box
        sx={{
          width: "3.6vh",
          height: "3.6vh",
          borderRadius: "50%",
          background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
          border: `1px solid ${t.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: FONT,
          fontWeight: 500,
          fontSize: "1.2vh",
          letterSpacing: "0.04em",
          color: t.textMuted,
        }}
      >
        VS
      </Box>
    </Box>
  );
};

/* Compact stat block used by OfficeScoreboard.
   Module-level so React doesn't re-create the component type on every parent
   render (avoids react/no-unstable-nested-components and the subtree remount
   it'd cause). */
const TeamBlock = ({ teamName, accent, isLeading, alignRight, dark }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: alignRight ? "flex-end" : "flex-start",
        textAlign: alignRight ? "right" : "left",
        flexShrink: 0,
        minWidth: "11vw",
      }}
    >
      {/* Sleek team name row — smaller dot, lighter weight, accent at 80%
          opacity (CC suffix). 3D crown replaces the emoji for the leader,
          centered above the dot + name. */}
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.6vw",
          flexDirection: alignRight ? "row-reverse" : "row",
        }}
      >
        {isLeading && (
          <Box
            sx={{
              position: "absolute",
              // Absolute-positioned above the name, so sizing it up floats
              // upward into the banner's open space — it never pushes the name.
              top: "-5.2vh",
              left: "50%",
              transform: "translateX(-50%)",
              // Bigger container with overflow visible so the spinning crown
              // never clips against its own box. Pointer-events off so it
              // never blocks clicks/hover behind it.
              width: "5.2vh",
              height: "5.2vh",
              overflow: "visible",
              pointerEvents: "none",
            }}
          >
            <Crown3D size="100%" />
          </Box>
        )}
        <Box
          sx={{
            width: "0.9vh",
            height: "0.9vh",
            borderRadius: "50%",
            background: accent,
            flexShrink: 0,
          }}
        />
        <Box
          component="span"
          sx={{
            fontFamily: FONT,
            // Lighter weight + lower opacity = quieter, more refined.
            // In light mode use the full-opacity accent so the name reads with
            // enough contrast against the light background (80% washed it out).
            fontWeight: 500,
            fontSize: "2.6vh",
            letterSpacing: "0.005em",
            color: dark ? `${accent}CC` : accent,
            lineHeight: 1.05,
            transition: "color 280ms ease",
          }}
        >
          {teamName}
        </Box>
      </Box>
    </Box>
  );
};

/** Small ±N pill rendered next to a team's points number.
 *  - Positive: green ↑N
 *  - Negative: red ↓N
 *  - Zero: hidden so the row stays uncluttered when there's no movement. */
/** Sleek ±N indicator — plain colored text, no pill, no arrow icon.
 *  Positive: muted green. Negative: muted red. Zero: hidden. */
const DeltaChip = ({ delta, dark }) => {
  if (!delta) return null;
  const positive = delta > 0;
  let fg;
  if (positive) {
    fg = dark ? "#5BC785" : "#16A34A";
  } else {
    fg = dark ? "#E08A8A" : "#DC2626";
  }
  return (
    <Box
      component="span"
      sx={{
        fontFamily: FONT,
        fontWeight: 500,
        fontSize: "1.25vh",
        letterSpacing: "0.01em",
        color: fg,
        fontFeatureSettings: '"tnum" 1',
        lineHeight: 1,
        opacity: 0.85,
      }}
    >
      {positive ? "+" : "−"}
      {Math.abs(delta)}
    </Box>
  );
};

const SHOW_TEAM_DELTAS = false;

const OfficeScoreboard = ({ stats, dark, perPlayer = false, title = "", icon = "" }) => {
  const t = tokens(dark);
  // Company setting (Settings → TV View): when `perPlayer` is on, the
  // comparison metric is each team's average points per player
  // (team points / number of team players) instead of the raw team total.
  // Keeps the comparison fair when teams have different headcounts.
  const metricOf = (teamStats) => {
    const pts = teamStats?.points || 0;
    const players = teamStats?.players || 0;
    return perPlayer && players > 0 ? pts / players : pts;
  };
  // Pick the two teams with the most points (excluding "Unassigned" so the
  // scoreboard shows actual competing teams). When fewer than two active
  // teams exist we render empty slots (no placeholder names) — an unfilled
  // side simply shows no team name rather than a seeded "Kitsap"/"Seattle".
  // Ranked by the active metric so the two teams shown stay consistent with
  // the numbers rendered (raw total or per-player average).
  const ranked = Object.entries(stats || {})
    .filter(([name]) => name && name !== "Unassigned")
    .map(([name, s]) => ({ name, ...s, metric: metricOf(s) }))
    .sort((a, b) => (b.metric || 0) - (a.metric || 0));
  const left = ranked[0] || { name: "", metric: 0, avgRevenue: 0, pctDelta: 0 };
  const right = ranked[1] || { name: "", metric: 0, avgRevenue: 0, pctDelta: 0 };
  // `points` here holds the DISPLAY metric (raw total or per-player average);
  // every downstream readout (numbers, %, bar, leader) uses it directly.
  // `pctDelta` is the day-over-day team momentum chip (Victor's DeltaChip).
  const k = { points: left.metric || 0, avgRevenue: left.avgRevenue || 0, pctDelta: left.pctDelta || 0 };
  const s = { points: right.metric || 0, avgRevenue: right.avgRevenue || 0, pctDelta: right.pctDelta || 0 };
  const rawTotalPts = (k.points || 0) + (s.points || 0);
  const totalPts = rawTotalPts || 1;
  // No points yet (e.g. no active teams) → keep the split neutral instead of
  // collapsing to a lopsided 0/100 bar.
  const kPct = rawTotalPts > 0 ? Math.round((k.points / totalPts) * 100) : 50;
  const sPct = 100 - kPct;
  const kAccent = teamColorFor(left.name);
  const sAccent = teamColorFor(right.name);

  return (
    <HudFrame
      accent={t.gold}
      dark={dark}
      sx={{ flexShrink: 0 }}
      contentSx={{
        // Padding tightened one more 25% pass — crown floats above the
        // frame's top edge (overflow:visible keeps it from clipping).
        p: "3.2vh 1.15vw 1vh",
        overflow: "visible",
        position: "relative",
      }}
    >
      <style>{`@keyframes tvWinningPop { 0% { transform: translate(-50%, -50%) scale(0.7); opacity: 0; } 60% { transform: translate(-50%, -50%) scale(1.08); opacity: 1; } 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } }`}</style>

      {/* Leaderboard title, centred on this frame (its own banner above is gone). */}
      {title && (
        <Box sx={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", mb: "1.4vh" }}>
          {icon && <Box sx={{ fontSize: "1.8vh", lineHeight: 1, mr: "0.6vw", opacity: 0.9 }}>{icon}</Box>}
          <Box
            component="span"
            sx={{
              fontFamily: MONO_FONT,
              fontWeight: 600,
              fontSize: "2vh",
              color: t.text,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Box>
        </Box>
      )}

      {/* Three-column row: Team A · Progress bar · Team B
          Bar takes flexible center space; team blocks are fixed-width on each side. */}
      {/* Layout: center-aligned so the team names sit on the same vertical
          line as the big points-comparison numbers in the center. The crown
          is absolute-positioned in each TeamBlock so it floats above the
          name without inflating its height. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "1.3vw",
          width: "100%",
        }}
      >
        <TeamBlock
          teamName={left.name}
          accent={kAccent}
          avgRevenue={k.avgRevenue}
          isLeading={k.points > s.points}
          dark={dark}
        />

        {/* Center: progress bar with points + % readouts above.
            Fixed width (not flex-grow) so it stays centered regardless of how
            wide each team name renders. */}
        <Box
          sx={{
            flexShrink: 0,
            width: "36vw",
            display: "flex",
            flexDirection: "column",
            gap: "0.5vh",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}
          >
            {/* LEFT: points number + optional ±N chip on top, percentage underneath.
                Number uses toLocaleString for comma separators. */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.05 }}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "0.6vw" }}>
                <Box
                  component="span"
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 500,
                    fontSize: "2.6vh",
                    color: dark ? "rgba(255,255,255,0.92)" : "rgba(15,23,42,0.92)",
                    letterSpacing: "-0.01em",
                    fontFeatureSettings: '"tnum" 1',
                  }}
                >
                  {Math.round(k.points || 0).toLocaleString()}
                </Box>
                {SHOW_TEAM_DELTAS && <DeltaChip delta={k.pctDelta} dark={dark} />}
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: FONT,
                  fontWeight: 500,
                  fontSize: "1.3vh",
                  color: `${kAccent}99`,
                  letterSpacing: "-0.005em",
                  fontFeatureSettings: '"tnum" 1',
                }}
              >
                {kPct}%
              </Box>
            </Box>
            <Label sx={{ color: t.textFaint, fontSize: "1.2vh", letterSpacing: "0.18em", textTransform: "uppercase" }}>
              {perPlayer ? "Avg pts / player" : "Points comparison"}
            </Label>
            {/* RIGHT: optional ±N chip + points number on top (mirrored), percentage underneath. */}
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", lineHeight: 1.05 }}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: "0.6vw" }}>
                {SHOW_TEAM_DELTAS && <DeltaChip delta={s.pctDelta} dark={dark} />}
                <Box
                  component="span"
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 500,
                    fontSize: "2.6vh",
                    color: dark ? "rgba(255,255,255,0.92)" : "rgba(15,23,42,0.92)",
                    letterSpacing: "-0.01em",
                    fontFeatureSettings: '"tnum" 1',
                  }}
                >
                  {Math.round(s.points || 0).toLocaleString()}
                </Box>
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: FONT,
                  fontWeight: 500,
                  fontSize: "1.3vh",
                  color: `${sAccent}99`,
                  letterSpacing: "-0.005em",
                  fontFeatureSettings: '"tnum" 1',
                }}
              >
                {sPct}%
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              height: "1.1vh",
              borderRadius: "999px",
              overflow: "hidden",
              background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
            }}
          >
            {/* Bar segments — slightly desaturated (CC alpha) and edges
                darken into transparency for a softer, less neon read. */}
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                left: "50%",
                top: "12%",
                bottom: "12%",
                width: 0,
                transform: "translateX(-50%)",
                borderLeft: `2px dashed ${
                  dark ? "rgba(255,255,255,0.55)" : "rgba(15,23,42,0.4)"
                }`,
                zIndex: 2,
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                width: `${kPct}%`,
                background: `linear-gradient(90deg, ${kAccent}CC, ${kAccent}99)`,
                transition: "width 600ms ease",
              }}
            />
            <Box
              sx={{
                width: `${sPct}%`,
                background: `linear-gradient(90deg, ${sAccent}99, ${sAccent}CC)`,
                transition: "width 600ms ease",
              }}
            />
            {/* Center marker — 1px hairline, translucent, no glow. */}
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                top: "-20%",
                bottom: "-20%",
                width: "1px",
                marginLeft: "-0.5px",
                background: dark ? "rgba(255,255,255,0.45)" : "rgba(15,23,42,0.35)",
                pointerEvents: "none",
                zIndex: 2,
              }}
            />
          </Box>
        </Box>

        <TeamBlock
          teamName={right.name}
          accent={sAccent}
          avgRevenue={s.avgRevenue}
          isLeading={s.points > k.points}
          alignRight
          dark={dark}
        />
      </Box>
    </HudFrame>
  );
};

const Scene1Leaderboard = ({ dark, topPlayers, kpiList, period, officeStats, yetiStats, lastMonthYetiBucket = null, workspaceName = "", perPlayer = false }) => {
  // Yeti benchmark per period for the leaderboard rows.
  //   today/week/month → matching UseYetiStats bucket.
  //   lastMonth        → use the server's stored historic Yeti amounts
  //     (lastMonthYetiBucket), built upstream from the same UseTopPlayers
  //     data PastLeaderboard renders on /home → Past Boards. Keeps the TV
  //     and Past Boards exactly in sync for completed prior months.
  const yetiBucket = useMemo(() => {
    if (period === "lastMonth") return lastMonthYetiBucket || {};
    return yetiStats?.[period] || {};
  }, [period, yetiStats, lastMonthYetiBucket]);
  const t = tokens(dark);
  const visible = useMemo(
    // Show all players regardless of team assignment — hard-cap at 16 as a
    // safety net so the table never runs unbounded into the OfficeScoreboard.
    () => (topPlayers || []).slice(0, 16),
    [topPlayers]
  );

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pt: "9vh",
        pb: "5vh",
        px: "3vw",
        display: "flex",
        flexDirection: "column",
        gap: "2vh",
      }}
    >
      {/* The leaderboard title used to sit in its own banner above everything,
          which read as a third stacked box. It now rides centred inside the
          scoreboard frame below, so the whole scene moves up and breathes. */}
      <OfficeScoreboard
        stats={officeStats}
        dark={dark}
        perPlayer={perPlayer}
        title={workspaceName ? `${workspaceName} Leaderboard` : "Technicians Leaderboard"}
        icon="⚒"
      />
      <style>{`@keyframes tvNewPointsPulse { 0%,100% { transform: scale(1); filter: brightness(1); } 50% { transform: scale(1.08); filter: brightness(1.4); } }`}</style>

      <HudFrame
        accent={t.accent}
        dark={dark}
        sx={{ flex: 1, minHeight: 0 }}
        contentSx={{ p: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        {/* Column headers — labels wrap within each cell; right padding clears live dot. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: LEADERBOARD_GRID,
            alignItems: "start",
            gap: "clamp(0.3vw, 0.6vw, 0.6vw)",
            px: "1.2vw",
            pr: "2.6vh",
            pt: "1.2vh",
            pb: "1vh",
            borderBottom: `1px solid ${t.hairline}`,
          }}
        >
          <HdrLabelCell dark={dark} textAlign="center">
            Rank
          </HdrLabelCell>
          <HdrLabelCell dark={dark}> </HdrLabelCell>
          <HdrLabelCell dark={dark}>Technician</HdrLabelCell>
          <HdrLabelCell dark={dark} textAlign="center">
            Team
          </HdrLabelCell>
          {(kpiList || []).slice(0, 5).map((kpi) => (
            <KpiMetricLabel key={`hdr-${kpi._id}`} kpi={kpi} dark={dark} textAlign="center" variant="header" />
          ))}
          {Array.from({ length: Math.max(0, 5 - (kpiList?.length || 0)) }).map((_, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={`hdrpad-${idx}`} />
          ))}
          <HdrLabelCell dark={dark} textAlign="center">
            Points
          </HdrLabelCell>
        </Box>
        {/* Rows share the height so a long roster still fits, but they are capped
            (see LeaderRow) so a short roster doesn't balloon into giant rows —
            leftover room is simply left empty at the bottom. */}
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "flex-start", py: "0.4vh", overflow: "hidden" }}>
          {visible.map((p, i) => (
            <LeaderRow
              key={p.userId || i}
              player={p}
              rank={i + 1}
              dark={dark}
              kpis={kpiList}
              isFirst={i === 0}
              yetiBucket={yetiBucket}
            />
          ))}
          {visible.length === 0 && (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Text sx={{ color: t.textFaint, fontSize: "2vh" }}>Waiting for live scores…</Text>
            </Box>
          )}
        </Box>
      </HudFrame>
    </Box>
  );
};

/* ---------- Scene 2: Company Pulse ---------- */
/* Mountain curve matches the dashboard MiniChart shape (CHART_POINTS) */
const MOUNTAIN_POINTS = [0, 0.35, 0.25, 0.5, 1.0, 0.65, 1.25, 1.15, 1.5];

/** Convert a series of [x,y] points to a smoothed Catmull-Rom-ish bezier path. */
const buildSmoothPath = (pts) => {
  if (pts.length < 2) return "";
  const d = [`M ${pts[0][0]} ${pts[0][1]}`];
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d.push(`C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2[0]} ${p2[1]}`);
  }
  return d.join(" ");
};

const MountainSparkline = ({ value, target, color, dark, ariaId }) => {
  const t = tokens(dark);
  const W = 200;
  const H = 60;
  const padX = 6;
  const padY = 6;
  const usableW = W - padX * 2;
  const usableH = H - padY * 2;
  const max = Math.max(...MOUNTAIN_POINTS);

  const ptsXY = MOUNTAIN_POINTS.map((p, i) => [
    padX + (i / (MOUNTAIN_POINTS.length - 1)) * usableW,
    padY + usableH - (p / max) * usableH,
  ]);

  const path = buildSmoothPath(ptsXY);
  const area = `${path} L ${padX + usableW} ${H - padY} L ${padX} ${H - padY} Z`;

  // Place markers along the curve based on value/target relative to the peak (max).
  const peakValue = Math.max(value || 0, target || 0, 1);
  const findPointAt = (v) => {
    const t01 = Math.max(0, Math.min(1, (v || 0) / peakValue));
    const idx = t01 * (ptsXY.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.min(lo + 1, ptsXY.length - 1);
    const frac = idx - lo;
    return [
      ptsXY[lo][0] + (ptsXY[hi][0] - ptsXY[lo][0]) * frac,
      ptsXY[lo][1] + (ptsXY[hi][1] - ptsXY[lo][1]) * frac,
    ];
  };
  const [px, py] = findPointAt(value || 0);
  const [yx, yy] = findPointAt(target || 0);

  const strokeColor = color || t.accent;
  const gradId = `tvgrad-${ariaId || Math.random().toString(36).slice(2, 7)}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: "100%", display: "block" }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={strokeColor} stopOpacity="0.35" />
          <stop offset="100%" stopColor={strokeColor} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path
        d={path}
        fill="none"
        stroke={strokeColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Yeti marker (target) */}
      <circle cx={yx} cy={yy} r="3.2" fill={dark ? "#0B1320" : "#FFFFFF"} stroke={t.textMuted} strokeWidth="1.4" />
      {/* Team marker (value) */}
      <circle cx={px} cy={py} r="4.2" fill={strokeColor} stroke={dark ? "#0B1320" : "#FFFFFF"} strokeWidth="1.6" />
    </svg>
  );
};

const KpiTile = ({ kpi, value, target, dark }) => {
  const t = tokens(dark);
  const delta = (value || 0) - (target || 0);
  const pct = target ? Math.round((delta / target) * 100) : 0;
  const positive = delta >= 0;
  const positiveBg = dark ? "rgba(60,198,138,0.14)" : "rgba(60,198,138,0.12)";
  const negativeBg = dark ? "rgba(225,108,108,0.14)" : "rgba(225,108,108,0.12)";
  const chipBg = positive ? positiveBg : negativeBg;
  const lineColor = positive ? t.positive : t.negative;
  // Values now flow in as KPI amounts (dollars or counts), not points.
  // Money-shaped KPIs get a $ prefix; everything else renders as a plain
  // number so reviews / yard signs / etc. read naturally.
  const monetary = isMonetaryKpi(kpi);
  const fmtAmt = (n) => {
    const rounded = Math.round(n || 0);
    const sign = rounded < 0 ? "-" : "";
    const abs = Math.abs(rounded);
    return monetary ? `${sign}$${formatNumber(abs)}` : `${sign}${formatNumber(abs)}`;
  };

  return (
    <Box
      sx={{
        position: "relative",
        background: t.surfaceStrong,
        border: `1px solid ${t.border}`,
        borderRadius: "18px",
        p: "1.4vh 1.1vw",
        display: "flex",
        flexDirection: "column",
        gap: "0.6vh",
        minHeight: "13vh",
        overflow: "hidden",
        boxShadow: dark
          ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.18)"
          : "0 1px 0 rgba(255,255,255,0.6) inset, 0 6px 18px rgba(15,23,42,0.06)",
      }}
    >
      {/* Top row: title + delta chip */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5vw" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "0.3vh", minWidth: 0, flex: 1 }}>
          <KpiMetricLabel kpi={kpi} dark={dark} textAlign="left" variant="tile" showGuideText={false} />
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "0.35vw", mt: "0.15vh" }}>
            <Display sx={{ fontSize: "3.4vh", color: t.text }}>
              {fmtAmt(value)}
            </Display>
          </Box>
        </Box>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.2vw",
            px: "0.55vw",
            py: "0.4vh",
            borderRadius: "999px",
            background: chipBg,
            color: positive ? t.positive : t.negative,
            fontFamily: FONT,
            fontWeight: 500,
            fontSize: "1vh",
            letterSpacing: "0.04em",
            flexShrink: 0,
          }}
        >
          {positive ? "▲" : "▼"} {Math.abs(pct)}%
        </Box>
      </Box>

      {/* Mountain curve */}
      <Box sx={{ flex: 1, minHeight: "4.5vh", mt: "0.3vh" }}>
        <MountainSparkline
          value={value}
          target={target}
          color={lineColor}
          dark={dark}
          ariaId={kpi._id}
        />
      </Box>

      {/* Footer row: vs Yeti */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: "0.4vh",
          borderTop: `1px solid ${t.hairline}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.3vw" }}>
          <Box
            sx={{
              width: "0.9vh",
              height: "0.9vh",
              borderRadius: "50%",
              background: dark ? "#0B1320" : "#FFFFFF",
              border: `1.5px solid ${t.textMuted}`,
            }}
          />
          <Label sx={{ color: t.textFaint, fontSize: "0.95vh" }}>
            Yeti {fmtAmt(target)}
          </Label>
        </Box>
        <Label sx={{ color: positive ? t.positive : t.negative, fontSize: "0.95vh", fontWeight: 500 }}>
          {positive ? "+" : ""}
          {fmtAmt(delta)}
        </Label>
      </Box>
    </Box>
  );
};

/* ---------- 3D Cylindrical Podium ---------- */
const PALETTE = {
  1: { top: "#F1F4F8", topHi: "#FFFFFF", front: "#C4CAD2", frontDark: "#949BA5", side: "#787F8A", glow: "#D8DDE3" },
  2: { top: "#DCE1E8", topHi: "#F4F6FA", front: "#A8B0BD", frontDark: "#7B8493", side: "#5E6675", glow: "#B8C0CC" },
  3: { top: "#F1C29A", topHi: "#FBE0C7", front: "#B97A4D", frontDark: "#8C5630", side: "#643D20", glow: "#C58A5A" },
};

const PEDESTAL_HEIGHTS = { 1: 14, 2: 11, 3: 9 }; // vh — cylinder body height

const IsoPedestal = ({ rank, dark, animDelay = 0 }) => {
  const heightVh = PEDESTAL_HEIGHTS[rank];
  const c = PALETTE[rank];
  const RANK_LABELS = { 1: "1", 2: "2", 3: "3" };
  // Body color: black with a vertical curve gradient to fake the cylindrical shading.
  // Body color: deep navy matching the mountain (#14304F shadow → #2C548A lit).
  // Edges darker, middle lighter — fakes the cylindrical curve.
  const bodyGradient = dark
    ? "linear-gradient(90deg, #0A1F38 0%, #14304F 22%, #2C548A 50%, #14304F 78%, #0A1F38 100%)"
    : "linear-gradient(90deg, #14304F 0%, #2C548A 30%, #4A7BC0 50%, #2C548A 70%, #14304F 100%)";
  const bottomGradient = dark
    ? "linear-gradient(180deg, #14304F 0%, #0A1F38 100%)"
    : "linear-gradient(180deg, #2C548A 0%, #14304F 100%)";

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: `${heightVh}vh`,
        animation: `tvPodiumRise 700ms ${animDelay}ms cubic-bezier(0.2, 0.8, 0.2, 1) both`,
      }}
    >
      {/* TOP — colored metallic disc (the rim a person stands on) */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "26%",
          borderRadius: "50%",
          background: `radial-gradient(ellipse at 50% 35%, ${c.topHi} 0%, ${c.top} 50%, ${c.frontDark} 100%)`,
          boxShadow: `inset 0 -3px 6px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.3) inset`,
          zIndex: 3,
        }}
      />

      {/* BODY — black cylinder with vertical-gradient shading + big rank number */}
      <Box
        sx={{
          position: "absolute",
          top: "13%", // half of top ellipse height — sits flush under top
          left: 0,
          right: 0,
          bottom: "13%", // half of bottom ellipse height
          background: bodyGradient,
          boxShadow: `0 8px 18px ${dark ? "rgba(0,0,0,0.5)" : "rgba(15,23,42,0.25)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          zIndex: 1,
        }}
      >
        {/* Big rank numeral — centered on the body */}
        <Box
          component="span"
          sx={{
            position: "relative",
            fontFamily: FONT,
            fontWeight: 500,
            fontSize: ({ 1: "3.8vh", 2: "3vh", 3: "2.6vh" }[rank]),
            color: c.front,
            letterSpacing: "-0.03em",
            lineHeight: 1,
            fontFeatureSettings: '"tnum" 1, "lnum" 1',
            textShadow: `0 2px 6px rgba(0,0,0,0.5), 0 0 12px ${c.glow}55`,
          }}
        >
          {RANK_LABELS[rank]}
        </Box>

        {/* Sheen sweep across body — animated highlight */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: "-30%",
            width: "40%",
            height: "100%",
            background: "linear-gradient(105deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0) 100%)",
            animation: `tvPodiumSheen ${6 + rank * 0.4}s ease-in-out ${animDelay + 800}ms infinite`,
          }}
        />
      </Box>

      {/* BOTTOM — front-facing curve at the base (front half of an ellipse) */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "26%",
          borderRadius: "50%",
          background: bottomGradient,
          clipPath: "inset(50% 0 0 0)",
          zIndex: 2,
        }}
      />

      {/* Floor reflection — soft elliptical glow under the cylinder */}
      <Box
        sx={{
          position: "absolute",
          left: "-4%",
          right: "-4%",
          bottom: "-1.4vh",
          height: "2vh",
          background: `radial-gradient(ellipse at center, ${c.glow}55 0%, transparent 65%)`,
          filter: "blur(5px)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />
    </Box>
  );
};

const PodiumPlayer = ({ rank, player, dark, animDelay }) => {
  const t = tokens(dark);
  const c = PALETTE[rank];

  const computeName = (p) => {
    if (!p) return "—";
    const stitched = `${p.firstName || ""} ${p.lastName || ""}`.trim();
    if (stitched) return stitched;
    return p.email || "—";
  };
  const fullName = computeName(player);

  const avatarSize = rank === 1 ? "5vh" : "4.2vh";
  const ringColor = c.glow;

  const totalPts = player?.scores?.total || 0;

  // Avatar overlaps the cylinder's top disc — it sits ON the platform.
  const avatarOnCylinder = (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: avatarSize,
        height: avatarSize,
        borderRadius: "50%",
        padding: "2px",
        background: `conic-gradient(from 220deg, ${ringColor}, ${c.front}, ${ringColor})`,
        boxShadow: `0 0 0 2px ${dark ? "rgba(11,19,32,0.7)" : "rgba(255,255,255,0.7)"}, 0 6px 14px ${ringColor}66`,
        zIndex: 4,
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: "50%",
          overflow: "hidden",
          background: dark ? "#26272A" : "#EEF2F8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "& > *": {
            width: "100% !important",
            height: "100% !important",
            fontSize: `${rank === 1 ? 1.8 : 1.5}vh`,
          },
        }}
      >
        {player ? (
          <SoftProfile src={player.image} name={fullName} fullCover />
        ) : null}
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "0.6vh",
        minWidth: 0,
        opacity: player ? 1 : 0.4,
        animation: player
          ? `tvPodiumRise 700ms ${animDelay}ms cubic-bezier(0.2, 0.8, 0.2, 1) both`
          : "none",
      }}
    >
      {/* Name only — rank subtitle (CHAMPION/RUNNER-UP/THIRD) removed; the
          pedestal numbers already convey the rank visually. */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.1vh", maxWidth: "100%" }}>
        <Text
          weight={500}
          sx={{
            fontSize: rank === 1 ? "1.5vh" : "1.3vh",
            color: t.text,
            letterSpacing: "-0.005em",
            textAlign: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "10vw",
            lineHeight: 1.15,
          }}
        >
          {fullName}
        </Text>
      </Box>

      {/* Cylinder pedestal with avatar standing on top */}
      <Box
        sx={{
          width: "96%",
          position: "relative",
          mt: rank === 1 ? "2.5vh" : "2.1vh",
        }}
      >
        {avatarOnCylinder}
        <IsoPedestal rank={rank} dark={dark} animDelay={animDelay} />
      </Box>
    </Box>
  );
};

/* Single soft spotlight that originates from the very top of the parent
   container (the HudFrame) and shines down onto cylinder #1. Subtle — pool
   of light, not a stage flood. */
const ChampionSpotlight = ({ dark }) => {
  const beamRgb = dark ? "255,255,255" : "255,232,170";
  const lampGlow = dark ? "rgba(255,255,255,0.85)" : "rgba(255,225,160,0.95)";
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        pointerEvents: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        zIndex: 0,
      }}
    >
      {/* Lamp dot — narrow source at the top edge of the container */}
      <Box
        sx={{
          width: "1.8vh",
          height: "0.5vh",
          borderRadius: "50%",
          background: `radial-gradient(ellipse at center, ${lampGlow} 0%, rgba(${beamRgb},0.55) 60%, transparent 100%)`,
          boxShadow: `0 0 14px ${lampGlow}, 0 0 28px rgba(${beamRgb},0.55)`,
          mt: "0.2vh",
          flexShrink: 0,
        }}
      />
      {/* Cone — narrow at lamp, sprays wide across the whole container at the
          bottom. Polygon spreads from a tight 1% slit to the full width. */}
      <Box
        sx={{
          width: "100%",
          flex: 1,
          background: `linear-gradient(180deg, rgba(${beamRgb},0.10) 0%, rgba(${beamRgb},0.06) 35%, rgba(${beamRgb},0.03) 70%, rgba(${beamRgb},0) 100%)`,
          clipPath: "polygon(49.4% 0%, 50.6% 0%, 100% 100%, 0% 100%)",
          filter: "blur(2.5px)",
          mt: "0.1vh",
        }}
      />
    </Box>
  );
};

const Podium3D = ({ entries, dark }) => {
  return (
    <Box
      sx={{
        flex: 1,
        position: "relative",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        gap: 0,
        minHeight: 0,
        pt: "0.4vh",
        pb: "0.8vh",
        overflow: "hidden",
      }}
    >
      {/* Tighter row — constrains width so cylinders feel like the inspiration */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          gap: 0,
          width: "100%",
          maxWidth: "32vw",
          mx: "auto",
        }}
      >
        <PodiumPlayer rank={2} player={entries[1]} dark={dark} animDelay={140} />
        <PodiumPlayer rank={1} player={entries[0]} dark={dark} animDelay={0} />
        <PodiumPlayer rank={3} player={entries[2]} dark={dark} animDelay={280} />
      </Box>

      {/* Floor shadow */}
      <Box
        sx={{
          position: "absolute",
          left: "30%",
          right: "30%",
          bottom: "0.2vh",
          height: "0.8vh",
          background: `radial-gradient(ellipse at center, ${dark ? "rgba(0,0,0,0.55)" : "rgba(15,23,42,0.18)"} 0%, transparent 70%)`,
          borderRadius: "999px",
          filter: "blur(3px)",
          pointerEvents: "none",
        }}
      />
      <style>{`@keyframes tvPodiumRise { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </Box>
  );
};

// Stable React node passed to KOTM as tvTrophyOverlay. Defined at module scope so
// it isn't recreated on every render (which would remount the WebGL Canvas).
const TV_TROPHY_OVERLAY = (
  <div
    xmlns="http://www.w3.org/1999/xhtml"
    style={{ width: "100%", height: "100%" }}
  >
    <Trophy3D size="100%" />
  </div>
);

const Scene2Pulse = ({ dark, kpiTiles, topPlayers, workspace, period }) => {
  const t = tokens(dark);
  const podiumPlayers = useMemo(
    () => (topPlayers || []).filter((p) => !p.isYeti).slice(0, 3),
    [topPlayers]
  );
  const trophyOverlayNode = TV_TROPHY_OVERLAY;

  /* Goal-based mountain — replaces KOTM (Yeti view) on TV */
  const [goal] = useCompanyGoal();
  const goalCurrent = useCompanyKpiTotal(goal?.kpiId, goal?.period || "month");
  const goalKpiTitle = useMemo(() => {
    if (!goal?.kpiId || !kpiTiles) return "";
    return kpiTiles.find((tile) => tile.id === goal.kpiId)?.kpi?.title || "";
  }, [goal?.kpiId, kpiTiles]);

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        pt: "9vh",
        pb: "5vh",
        px: "3vw",
        display: "flex",
        flexDirection: "column",
        gap: "2vh",
      }}
    >
      <SceneHeader index={1} total={2} title="Company Performance" icon="🏢" period={period} dark={dark} />

      <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "1.05fr 1fr", gap: "1.6vw", minHeight: 0 }}>
        {/* Goal Mountain — company-wide goal view (replaces KOTM on TV).
            KOTM internals untouched, dashboard unaffected — this is its own
            self-contained component (GoalMountain.jsx). */}
        <HudFrame
          accent={t.gold}
          dark={dark}
          contentSx={{ overflow: "hidden", gap: "1vh" }}
        >
          {/* SectionHeader renders the "Goal of the Mountain" title at the
              SAME size as the other panels (Team vs The Yeti, Workspace
              Champions). The 0/150 metric card lives INSIDE GoalMountain. */}
          <SectionHeader
            icon="⛰"
            title="Goal of the Mountain"
            hint={
              goal
                ? `${goalKpiTitle || "Goal"} · ${periodLabel(goal.period)}`
                : "Set a goal from the Company View"
            }
            dark={dark}
            accent={t.gold}
          />
          <Box sx={{ flex: 1, position: "relative", display: "flex", alignItems: "stretch", justifyContent: "stretch", minHeight: 0 }}>
            <GoalMountain
              darkMode={dark}
              noScroll
              transparentBg
              goalTarget={goal?.target || 0}
              goalCurrent={goalCurrent}
              goalKpiTitle={goalKpiTitle}
              goalPeriodLabel={periodLabel(goal?.period)}
              mountainImageKey={goal?.mountainImage || ""}
            />
          </Box>
        </HudFrame>

        {/* Right column */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "1.6vh", minHeight: 0 }}>
          <HudFrame
            accent={t.accent}
            dark={dark}
            sx={{ flex: 1.4 }}
            contentSx={{ gap: "0.4vh" }}
          >
            <SectionHeader
              icon="❄"
              title="Team vs The Yeti"
              hint={`${PERIOD_LABELS[period]} · live`}
              dark={dark}
              accent={t.accent}
            />
            <Box
              sx={{
                flex: 1,
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gridAutoRows: "1fr",
                gap: "0.9vw",
                minHeight: 0,
              }}
            >
              {kpiTiles.slice(0, 6).map((tile) => (
                <KpiTile key={tile.id} kpi={tile.kpi} value={tile.value} target={tile.target} trend={tile.trend} dark={dark} />
              ))}
            </Box>
          </HudFrame>

          {/* Top performers podium */}
          <HudFrame
            accent={t.gold}
            dark={dark}
            sx={{ flex: 1.1 }}
            contentSx={{ gap: "0.4vh", position: "relative" }}
          >
            {/* Single spotlight from the top edge of the container — shines
                down onto the champion cylinder. Sits behind the content. */}
            <ChampionSpotlight dark={dark} />
            <SectionHeader
              icon="♛"
              title="Top Contenders"
              hint={`${workspace?.name || "Top performers"} · this period`}
              dark={dark}
              accent={t.gold}
            />
            <Podium3D entries={podiumPlayers} dark={dark} />
          </HudFrame>
        </Box>
      </Box>
    </Box>
  );
};

/* ---------- Container ---------- */
// The two built-in scenes. Published dashboards join this list per workspace.
const SCENES = ["leaderboard", "pulse"];

const TVLeaderboard = () => {
  const [dark, setDark] = useState(true);
  const [periodIdx, setPeriodIdx] = useState(PERIODS.indexOf("month")); // this month (default)

  /* ---- Remote / keyboard focus navigation (spatial, TV-style) ----
     Focus moves to whatever is physically next to it in the pressed direction,
     the way a TV app works: Left/Right across a row, Up/Down between rows, and
     when you run out at the top you land on the top bar, at the bottom on the
     tab switcher. The board scrolls itself to follow the focus. Enter presses.
     The cue stays hidden until a key is pressed and auto-hides when idle. */
  const [navCue, setNavCue] = useState(false);
  const [focusInBoard, setFocusInBoard] = useState(false);
  const navIdleRef = useRef(null);
  const dashSceneRef = useRef(null);
  const focusElRef = useRef(null);
  const focusRectRef = useRef(null);
  const NAV_IDLE_MS = 12000;
  const period = PERIODS[periodIdx];
  const [sceneIdx, setSceneIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // Dashboards published to TVs ("Show on TV" on Analytics → Dashboards).
  const [tvBoards, setTvBoards] = useState([]);

  const dispatch = useDispatch();

  // Subscribe to the teams slice so color/list changes from Player
  // Management re-render the TV (instant within the same tab; cross-tab
  // requires a refresh / next poll until the saga picks up a server change).
  // `_teamsRev` is unused but the useSelector subscription is what triggers
  // re-renders when team rows change.
  // eslint-disable-next-line no-unused-vars
  const _teamsRev = UseTeams();
  // Kick off a team list load on mount in case this is opened in a fresh tab.
  useEffect(() => {
    dispatch(getTeams());
  }, [dispatch]);

  // "lastMonth" uses the historic-month branch of UseTopPlayers. The historic
  // KPI data is lazy-loaded — we have to dispatch a fetch the first time the
  // tab is selected, otherwise UseTopPlayers reads `undefined` and renders
  // nothing.
  const lastMonthMoment = useMemo(() => moment().subtract(1, "month").startOf("month"), []);
  const { getPreviousKpis } = useGetPreviousKpis();
  useEffect(() => {
    if (period === "lastMonth") getPreviousKpis(lastMonthMoment);
  }, [period, lastMonthMoment, getPreviousKpis]);
  const topPlayers = UseTopPlayers(
    period === "lastMonth" ? "month" : period,
    period === "lastMonth" ? lastMonthMoment : null,
  );
  const groupKpis = UseGroupKpis();
  const groupAllKpis = UseGroupAllKpis();
  const lastMonthKey = lastMonthMoment.format("YYYY-MM");
  const historicKpis = useSelector((state) =>
    period === "lastMonth" ? state.kpiReducer.historicKpis?.[lastMonthKey] : null,
  );
  const displayGroupKpis = useMemo(() => {
    const exclude = (kpi) => !isExcludedFromBoards(kpi, period === "lastMonth" ? lastMonthKey : null);
    if (period !== "lastMonth") {
      return (groupKpis || []).filter(exclude);
    }
    return filterKpisByUserScores(groupAllKpis, historicKpis).filter(exclude);
  }, [period, groupKpis, groupAllKpis, lastMonthKey, historicKpis]);
  // Scorekeeper-only metrics (e.g. Commission) stay OUT of everything the TV
  // renders — scoreboard KPI columns and the Team vs The Yeti tiles — but IN
  // `displayGroupKpis`, which the roster scoring above uses, so their points
  // still count toward each player's total. Same split the home dashboard
  // makes (UseLeaderboardGroupKpis for columns, UseTopPlayers for totals).
  const publicGroupKpis = useMemo(
    () => filterKpisForPublicDisplay(displayGroupKpis),
    [displayGroupKpis],
  );
  const company = UseCompany();
  const workspace = UseSelectedWorkspace();
  const workspaces = UseCompanyWorkspaces(true);
  const yetiStats = UseYetiStats();
  const allCompanyUsers = UseUsersFullData();

  // Hidden-player exclusion. The home Leaderboard treats user.isActive=false
  // (set via the doughnut chart's "Hide" tab) as hidden. The TV mirrors that
  // here, so hidden players don't pollute team standings, KPI sums, KOTM
  // top-3, workspace counts, or "Team vs Yeti" totals.
  const visibleCompanyUsers = useMemo(
    () =>
      Array.isArray(allCompanyUsers)
        ? allCompanyUsers.filter(isVisiblePlayer)
        : allCompanyUsers,
    [allCompanyUsers],
  );
  const visibleTopPlayers = useMemo(
    () =>
      Array.isArray(topPlayers)
        ? topPlayers.filter(
            (p) => isVisiblePlayer(p) && (p?.isYeti || (p?.scores?.total || 0) > 0),
          )
        : topPlayers,
    [topPlayers],
  );

  // Last-month yeti benchmark per KPI, sourced from the SAME data PastLeaderboard
  // (route: /home → Past Boards) uses: UseTopPlayers("month", monthRef) returns
  // the Yeti as one of the player entries with its server-computed `scores[kpiId].amt`
  // for that historic month. Wrapping that as a `{ kpiId: { amt } }` bucket keeps
  // the TV's last-month yeti row/tile identical to what Past Boards renders —
  // instead of synthesizing it from kpi.target × workingDays which drifts from
  // the server's stored historic amounts.
  const lastMonthYetiBucket = useMemo(() => {
    if (period !== "lastMonth") return null;
    const yeti = (topPlayers || []).find((p) => p?.isYeti);
    if (!yeti || !yeti.scores) return {};
    const bucket = {};
    Object.keys(yeti.scores).forEach((key) => {
      if (key === "total") return;
      const entry = yeti.scores[key] || {};
      bucket[key] = { amt: Number(entry.amt) || 0 };
    });
    return bucket;
  }, [period, topPlayers]);

  /* Build the leaderboard roster: every visible (non-archived) company user
     in scope for the active workspace — plus unassigned users regardless of
     workspace — hydrated with their KPI scores from `topPlayers` if
     available. Players with no scoring activity (0 points) are excluded
     below, whether they're assigned to a team or unassigned.

     Score scoping: mirrors the home dashboard's LiveLeaderboard, which
     filters each player's scores down to the workspace's KPIs (groupKpis)
     and recomputes `total` as the sum of `pts` across only those KPIs. That
     keeps TV ranks identical to dashboard ranks when a workspace runs a
     subset of the company-wide KPI set. */
  const teamLeaderboard = useMemo(() => {
    if (!Array.isArray(visibleCompanyUsers)) return visibleTopPlayers;
    // Index topPlayers by userId for O(1) lookup of scores.
    const byId = {};
    (visibleTopPlayers || []).forEach((p) => {
      if (p?.userId) byId[String(p.userId)] = p;
    });

    // Map of KPI id → KPI config for the active workspace. Used both to
    // scope each player's scores and to recompute pts from amt using the
    // rule the KPI settings dialog advertises:
    //   "If a player gets {pointValue}, they earn {pointScale} pts"
    //   → pts = amount × (pointScale / pointValue)
    //   → falls back to amount × pointScale when pointValue isn't set
    // Same formula UseYetiStats uses, so player rows and the Yeti row are
    // directly comparable on the TV.
    const kpiById = {};
    if (Array.isArray(displayGroupKpis)) {
      displayGroupKpis.forEach((k) => {
        if (k && typeof k === "object" && k._id) kpiById[k._id] = k;
      });
    }
    const workspaceKpiIds = new Set(Object.keys(kpiById));
    const scopeToWorkspaceKpis = (rawScores) => {
      if (!rawScores) return { total: 0 };
      if (workspaceKpiIds.size === 0) return rawScores;
      const scoped = { total: 0 };
      Object.keys(rawScores).forEach((key) => {
        if (key === "total") return;
        if (!workspaceKpiIds.has(key)) return;
        const original = rawScores[key];
        const kpi = kpiById[key];
        const amt = Number(original?.amt) || 0;
        const ptsScale = Number(kpi?.pointScale) || 0;
        const ptsValue = Number(kpi?.pointValue) || 0;
        const isAnchor = kpi?.metricType === "anchor";
        const ptsPerAmt = ptsScale > 0 ? ptsValue / ptsScale : ptsValue;
        let recomputedPts;
        if (!kpi) {
          recomputedPts = Number(original?.pts) || 0;
        } else if (isAnchor) {
          // Live periods: server's anchor pts are correct, recompute would corrupt.
          recomputedPts = Number(original?.pts) || 0;
        } else {
          // Use amt × (pointValue/pointScale) ONLY when BOTH are present and
          // non-zero; if EITHER is missing or zero, trust the server-stored
          // pts (the user's actual score) — same as /home's LiveLeaderboard
          // and /scorekeeper.
          const hasFullConfig = ptsValue > 0 && ptsScale > 0;
          recomputedPts = hasFullConfig
            ? amt * ptsPerAmt
            : (Number(original?.pts) || 0);
        }
        scoped[key] = { ...original, pts: recomputedPts };
      });
      scoped.total = Object.keys(scoped)
        .filter((k) => k !== "total")
        .reduce((sum, k) => sum + (scoped[k]?.pts || 0), 0);
      return scoped;
    };
    const recomputeScores = (rawScores) => {
      if (period === "lastMonth") {
        return recomputeScopedPlayerScores(rawScores, displayGroupKpis, {
          recomputeAnchorAsProgressive: true,
        });
      }
      return scopeToWorkspaceKpis(rawScores);
    };

    // Scope to the currently selected workspace — switching workspace from
    // the TV switcher should immediately re-scope the leaderboard to that
    // workspace's people.
    const activeGroupId = workspace?._id;
    const inWorkspace = visibleCompanyUsers
      .filter((u) => !u.isArchived)
      // Unassigned players (no groupId) always pass the workspace scope —
      // they aren't excluded for being unassigned, only for having 0 points
      // (see the total>0 filter below).
      .filter((u) => !activeGroupId || !u.groupId || u.groupId === activeGroupId);
    const rosterBase = inWorkspace
      .map((u) => {
        const tp = byId[String(u.userId)] || {};
        return {
          // Base profile fields from the user record (firstName, lastName, image, etc.)
          ...u,
          // Then KPI fields override from topPlayers when available.
          ...tp,
          // Scores scoped to workspace KPIs and `total` recomputed — same
          // formula as the home dashboard's LiveLeaderboard.
          scores: recomputeScores(tp.scores),
          userId: u.userId,
          // Always resolve through teamForPlayer so localStorage overrides win
          // over any stale backend value (incl. a legacy "Unassigned" string).
          team: teamForPlayer(u),
        };
      });

    // Mix Yeti into the sortable pool so it competes for rank like every
    // other player. teamForPlayer attaches the "The Yeti" team label so it
    // passes downstream team filters. Yeti's scores get the same workspace
    // scoping so its rank stays apples-to-apples with the real players.
    const yeti = (visibleTopPlayers || []).find((p) => p.isYeti);
    if (yeti) {
      rosterBase.push({
        ...yeti,
        scores: recomputeScores(yeti.scores),
        team: teamForPlayer(yeti),
      });
    }

    return rosterBase
      // Exclude players with no scoring activity (total of 0). Yeti always
      // stays as the benchmark row.
      .filter((p) => p?.isYeti || (p?.scores?.total || 0) > 0)
      .sort((a, b) => (b.scores?.total || 0) - (a.scores?.total || 0))
      .map((p, i) => ({ ...p, _rank: i + 1 }));
  }, [visibleCompanyUsers, visibleTopPlayers, workspace?._id, displayGroupKpis, _teamsRev, period]);

  const workspaceById = useMemo(() => {
    const m = {};
    (workspaces || []).forEach((w) => {
      m[w._id] = w;
    });
    return m;
  }, [workspaces]);

  // Player count per workspace — used by the TV workspace switcher cards.
  // Counts only visible (non-hidden) players so the switcher matches what
  // the TV roster actually shows.
  const playerCountsByWorkspace = useMemo(() => {
    const counts = {};
    (visibleCompanyUsers || []).forEach((u) => {
      if (u?.isArchived) return;
      if (!u?.groupId) return;
      counts[u.groupId] = (counts[u.groupId] || 0) + 1;
    });
    return counts;
  }, [visibleCompanyUsers]);

  /* Aggregate top players → workspace scoreboard */
  const workspaceScores = useMemo(() => {
    if (!visibleTopPlayers) return [];
    const agg = {};
    visibleTopPlayers.forEach((p) => {
      if (p.isYeti || !p.groupId) return;
      const w = workspaceById[p.groupId];
      if (!w) return;
      if (!agg[p.groupId]) {
        agg[p.groupId] = { id: p.groupId, name: w.name, points: 0, players: 0, revenue: 0 };
      }
      agg[p.groupId].points += p?.scores?.total || 0;
      agg[p.groupId].players += 1;
    });
    Object.values(agg).forEach((entry) => {
      entry.revenue = entry.players > 0 ? entry.points / entry.players : 0;
    });
    return Object.values(agg).sort((a, b) => b.points - a.points);
  }, [visibleTopPlayers, workspaceById]);

  /* Aggregate the leaderboard rows → team scoreboard. Built dynamically from
     whatever distinct teams exist on the rows currently displayed. No seeded
     placeholder teams — only real, assigned team names accumulate, so the
     scoreboard shows nothing until players are actually grouped into teams.
     Source is `teamLeaderboard`, NOT the company-wide `visibleTopPlayers`:
     the on-screen leaderboard is already scoped to the active workspace
     (and its scores re-totaled against workspace KPIs), so summing from
     there keeps the bar totals consistent with the rows the viewer sees.
     Pulling from `visibleTopPlayers` would inflate a team's points by
     including same-team players in other workspaces. */
  const officeStats = useMemo(() => {
    const init = () => ({
      points: 0,
      todayPoints: 0,
      players: 0,
      revenueTotal: 0,
      avgRevenue: 0,
    });
    // Start empty — only real, assigned teams accumulate here. No seeded
    // "Kitsap"/"Seattle" placeholders, so the scoreboard stays blank until
    // players are actually assigned to a team.
    const stats = {};
    if (!teamLeaderboard || !displayGroupKpis) return stats;
    const revenueKpi = displayGroupKpis.find((k) => /revenue/i.test(k.title || ""));
    teamLeaderboard.forEach((p) => {
      if (p.isYeti) return;
      const office = teamForPlayer(p);
      if (!stats[office]) stats[office] = init();
      stats[office].points += p?.scores?.total || 0;
      stats[office].todayPoints += p?.today || 0;
      stats[office].players += 1;
      if (revenueKpi) {
        stats[office].revenueTotal += p?.scores?.[revenueKpi._id]?.amt || 0;
      }
    });
    // Compute day-over-day percentage delta per team so it is ready if the
    // hidden ±N indicators are enabled again.
    //   yesterday_team_points = total - today
    //   yesterday_pct         = yesterday_team / yesterday_total
    //   today_pct             = today_team / today_total
    //   pctDelta              = today_pct - yesterday_pct  (rounded)
    // The two deltas always sum to ~0 (one team's gain is the other's loss).
    const todayTotal = Object.values(stats).reduce((acc, s) => acc + s.points, 0) || 0;
    const yTotal = Object.values(stats).reduce(
      (acc, s) => acc + Math.max(0, s.points - s.todayPoints),
      0,
    ) || 0;
    Object.values(stats).forEach((s) => {
      s.avgRevenue = s.players > 0 ? s.revenueTotal / s.players : 0;
      const yPts = Math.max(0, s.points - s.todayPoints);
      const yPct = yTotal > 0 ? (yPts / yTotal) * 100 : 0;
      const tPct = todayTotal > 0 ? (s.points / todayTotal) * 100 : 0;
      s.pctDelta = Math.round(tPct - yPct);
    });
    return stats;
  }, [teamLeaderboard, displayGroupKpis, _teamsRev]);

  /* KPI tiles for Scene 2 — "Team vs The Yeti" panel.
     Values are AVERAGES expressed in the KPI's natural unit (dollars / count),
     mirroring the home dashboard's "Team Performance VS. The Yeti" panel which
     compares amounts not points. Same formula as UseMyKpis (hooks/kpi.js):
     the team's total for the KPI divided ONLY by the active players who have
     an entry for THIS metric in the period — players who never contributed
     to a KPI must not dilute its average. Dividing by the whole roster made
     the TV disagree with /home (e.g. Technicians Up Sell $1,194 vs $1,591).
     Yeti is dropped so it doesn't skew the average. The result is rounded. */
  const kpiTiles = useMemo(() => {
    if (!publicGroupKpis) return [];
    const activePlayers = (topPlayers || []).filter(
      (p) => isVisiblePlayer(p) && !p.isYeti,
    );
    return publicGroupKpis.map((kpi) => {
      let sumAmt = 0;
      let scorers = 0;
      activePlayers.forEach((p) => {
        // Same scorer test as UseMyKpis: the score bucket exists for this KPI
        // (truthy object), not amt > 0 — keeps the two panels identical.
        const bucket = p?.scores?.[kpi._id];
        if (bucket) scorers += 1;
        sumAmt += bucket?.amt || 0;
      });
      const avgAmt = scorers > 0 ? Math.round(sumAmt / scorers) : 0;
      // Yeti benchmark per period:
      //   today/week/month → use the matching bucket from UseYetiStats
      //     (already proportional to elapsed business days).
      //   lastMonth → use the server's stored historic Yeti amounts from
      //     `lastMonthYetiBucket` (derived from the same UseTopPlayers data
      //     PastLeaderboard on /home → Past Boards renders). Falls back to 0
      //     if the historic fetch hasn't populated yet.
      const yetiAmt = period === "lastMonth"
        ? Number(lastMonthYetiBucket?.[kpi._id]?.amt) || 0
        : yetiStats?.[period]?.[kpi._id]?.amt || 0;
      const trend = []; // No per-KPI history available; sparkline degrades to hairline
      return {
        id: kpi._id,
        kpi,
        value: avgAmt,
        target: yetiAmt,
        trend,
      };
    });
  }, [publicGroupKpis, topPlayers, yetiStats, period, lastMonthYetiBucket]);

  /* Refresh KPIs periodically. Also fire once on mount so the TV doesn't show
     stale data for a full interval after navigation, and re-fire when the tab
     regains visibility/focus so a TV that was minimized catches up instantly. */
  useEffect(() => {
    const refresh = () => dispatch(refreshKpis());
    refresh();
    const id = setInterval(refresh, REFRESH_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refresh);
    };
  }, [dispatch]);

  /* Published dashboards → extra scenes for THIS workspace's rotation.
     Refetched on the same cadence the TV already refreshes KPIs. */
  useEffect(() => {
    let alive = true;
    const load = () =>
      GET_DASHBOARDS()
        .then((res) => {
          if (alive) setTvBoards((Array.isArray(res.data) ? res.data : []).filter((d) => d.showOnTv));
        })
        .catch(() => {});
    load();
    const id = setInterval(load, REFRESH_INTERVAL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  /* Which published boards this TV plays. A new dashboard belongs to NOBODY
     until someone assigns it to a workspace in Settings -> TV View — so an
     unassigned board (empty tvWorkspaceIds) shows on NO TV, rather than
     defaulting onto every one (which duplicated the built-in default board and
     put a stranger's board on every screen). A board appears here only once its
     tvWorkspaceIds explicitly includes this workspace. Sharing a board with
     people is a separate decision and never gates this. */
  const boardViews = useMemo(
    () =>
      tvBoards.filter((d) => {
        const ids = (d.tvWorkspaceIds || []).map(String);
        // Empty = unassigned → belongs to nobody, shows on no TV. "Every
        // workspace" is stored as the explicit list of workspace ids (chosen in
        // TV View settings), so a board appears only where it's actually assigned.
        return ids.includes(String(workspace?._id));
      }),
    [tvBoards, workspace?._id]
  );

  /* The TV's views. A VIEW is a whole screen with its own pages — they never
     merge. The built-in view owns the two rotating scenes; each published
     dashboard is its own view with a single page. */
  /* A workspace can switch the built-in board off (Group.tvShowDefaultBoard),
     but only when it has another view — a TV must never be left with nothing. */
  const showsBuiltin = workspace?.tvShowDefaultBoard !== false || !boardViews.length;
  const viewList = useMemo(
    () => [...(showsBuiltin ? ["builtin"] : []), ...boardViews.map((d) => `board:${d._id}`)],
    [boardViews, showsBuiltin]
  );

  /* Which view this TV is on, remembered per workspace on the device. */
  const viewKey = `gmb_tv_view_${workspace?._id || "all"}`;
  const [viewId, setViewId] = useState("builtin");
  useEffect(() => {
    let saved = "";
    try { saved = localStorage.getItem(viewKey) || ""; } catch (e) { saved = ""; }
    // Honor a saved board even when it isn't in this workspace's assigned list:
    // "Open on TV" is a preview, so any real board id should show. An unassigned
    // board that isn't in `viewList` is fetched by id below (previewBoard) so it
    // renders instead of silently falling back to the native scene ("just Yeti").
    if (saved === "builtin" || saved.startsWith("board:")) setViewId(saved);
    else setViewId(viewList[0] || "builtin");
  }, [viewKey, viewList]);

  /* A saved board that isn't in `boardViews` (an unassigned board opened on this
     TV) is fetched by id here so it still renders. Assigned boards come straight
     from boardViews and never trigger a fetch. */
  const [previewBoard, setPreviewBoard] = useState(null);
  useEffect(() => {
    if (!viewId.startsWith("board:") || boardViews.some((d) => `board:${d._id}` === viewId)) {
      setPreviewBoard(null);
      return undefined;
    }
    const id = viewId.slice("board:".length);
    let alive = true;
    GET_DASHBOARD(id)
      .then((res) => { if (alive) setPreviewBoard(res && res.data ? res.data : null); })
      .catch(() => { if (alive) setPreviewBoard(null); });
    return () => { alive = false; };
  }, [viewId, boardViews]);

  const activeBoard =
    viewId.startsWith("board:")
      ? (boardViews.find((d) => `board:${d._id}` === viewId)
          || (previewBoard && `board:${previewBoard._id}` === viewId ? previewBoard : null))
      : null;
  // Pages belong to the view: the built-in has two, a board view has one.
  const pageCount = activeBoard ? 1 : SCENES.length;

  /* Dashboards combined on ONE TV. Every entry in `viewList` (the built-in board
     plus each dashboard assigned to this workspace) becomes a switchable tab.
     Labels: built-in reads "Leaderboard"; a dashboard reads its own name. */
  const tabMeta = useMemo(
    () => viewList.map((v) => {
      if (v === "builtin") return { id: v, label: "Leaderboard" };
      const b = boardViews.find((d) => `board:${d._id}` === v)
        || (previewBoard && `board:${previewBoard._id}` === v ? previewBoard : null);
      return { id: v, label: (b && (b.name || "").trim()) || "Dashboard" };
    }),
    [viewList, boardViews, previewBoard],
  );
  /* Switch the active dashboard and remember it on this device (so a reboot
     resumes where it was). Reset to the view's first page. */
  const selectView = useCallback((v) => {
    setViewId(v);
    try { localStorage.setItem(viewKey, v); } catch (e) { /* private mode */ }
    setSceneIdx(0);
  }, [viewKey]);

  // A board view can pin its own dark/light; the built-in follows the TV button.
  const effectiveDark = activeBoard && activeBoard.tvTheme && activeBoard.tvTheme !== "follow"
    ? activeBoard.tvTheme === "dark"
    : dark;

  // Workspace switch / unpublish can shrink the list — snap back in range.
  useEffect(() => {
    if (sceneIdx >= pageCount) setSceneIdx(0);
  }, [pageCount, sceneIdx]);

  /* Auto-rotate the ACTIVE view's pages. A one-page view simply sits still. */
  const sceneTimerRef = useRef(null);
  useEffect(() => {
    if (paused || pageCount < 2) return undefined;
    sceneTimerRef.current = setInterval(() => {
      setSceneIdx((i) => (i + 1) % pageCount);
    }, SCENE_DURATION_MS);
    return () => clearInterval(sceneTimerRef.current);
  }, [paused, sceneIdx, pageCount]);

  /* Remote control */
  const goNext = useCallback(() => setSceneIdx((i) => (i + 1) % pageCount), [pageCount]);
  const goPrev = useCallback(() => setSceneIdx((i) => (i - 1 + pageCount) % pageCount), [pageCount]);

  // Reveal the focus cue and keep it alive; it fades after NAV_IDLE_MS of no input.
  const bumpIdle = useCallback(() => {
    if (navIdleRef.current) clearTimeout(navIdleRef.current);
    navIdleRef.current = setTimeout(() => setNavCue(false), NAV_IDLE_MS);
  }, []);

  /* Everything on screen the remote can land on: the top bar's controls, the
     tab switcher, and — the part that was unreachable before — the controls
     INSIDE the dashboard cards (filter chips, dropdowns). Off-screen items are
     kept: moving to one scrolls the board to it. */
  const navCandidates = useCallback(() => {
    const SEL = 'button, [role="button"], a[href], select, [data-navfocus]';
    return Array.from(document.querySelectorAll(SEL)).filter((el) => {
      if (el.disabled || el.getAttribute("aria-hidden") === "true") return false;
      if (el.closest("[data-nav-skip]")) return false;
      const r = el.getBoundingClientRect();
      if (r.width < 14 || r.height < 14) return false;
      if (r.width > window.innerWidth * 0.92 && r.height > window.innerHeight * 0.5) return false;
      const cs = window.getComputedStyle(el);
      if (cs.visibility === "hidden" || cs.display === "none" || Number(cs.opacity) === 0) return false;
      return true;
    });
  }, []);

  /* Move focus to the nearest thing in a direction, two passes:
       1. anything that lines up with you on the other axis (same row going
          sideways, same column going up/down) — closest one wins;
       2. if nothing lines up, the closest thing in that direction overall.
     Pass 2 matters on a dashboard: dropping out of a card's left column still
     finds the filter chips off to the right instead of skipping the whole card. */
  const pickInDirection = useCallback((dir, from, filter) => {
    const fcx = from.left + from.width / 2;
    const fcy = from.top + from.height / 2;
    let aligned = null; let alignedScore = Infinity;
    let any = null; let anyScore = Infinity;

    navCandidates().forEach((el) => {
      if (filter && !filter(el)) return;
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      let primary;
      let cross;
      let overlap;
      if (dir === "right") {
        if (r.left < from.right - 4) return;
        primary = r.left - from.right; cross = Math.abs(cy - fcy);
        overlap = Math.min(r.bottom, from.bottom) - Math.max(r.top, from.top);
      } else if (dir === "left") {
        if (r.right > from.left + 4) return;
        primary = from.left - r.right; cross = Math.abs(cy - fcy);
        overlap = Math.min(r.bottom, from.bottom) - Math.max(r.top, from.top);
      } else if (dir === "down") {
        if (r.top < from.bottom - 4) return;
        primary = r.top - from.bottom; cross = Math.abs(cx - fcx);
        overlap = Math.min(r.right, from.right) - Math.max(r.left, from.left);
      } else {
        if (r.bottom > from.top + 4) return;
        primary = from.top - r.bottom; cross = Math.abs(cx - fcx);
        overlap = Math.min(r.right, from.right) - Math.max(r.left, from.left);
      }
      if (primary < 0) primary = 0;
      if (overlap > 0 && primary + cross * 0.15 < alignedScore) {
        alignedScore = primary + cross * 0.15; aligned = el;
      }
      const loose = primary + cross * 0.5;
      if (loose < anyScore) { anyScore = loose; any = el; }
    });
    return aligned || any;
  }, [navCandidates]);

  const setFocusEl = useCallback((el) => {
    if (!el) return;
    document.querySelectorAll(".tvnav-foc").forEach((n) => n.classList.remove("tvnav-foc"));
    el.classList.add("tvnav-foc");
    focusElRef.current = el;
    const scene = dashSceneRef.current;
    const inBoard = !!(scene && scene.contains(el));
    setFocusInBoard(inBoard);
    // Never "scroll into view" the board itself — that would undo the very
    // scroll that parked the ring there.
    const isBoardRoot = !!(scene && scene.root && scene.root() === el);
    if (inBoard && !isBoardRoot && scene.ensureVisible) scene.ensureVisible(el);
    // rect settles after any scroll this caused
    requestAnimationFrame(() => { focusRectRef.current = el.getBoundingClientRect(); });
    focusRectRef.current = el.getBoundingClientRect();
  }, []);

  // Metrics re-render constantly and can replace the focused node — re-acquire
  // the nearest control to where focus was so the ring never silently vanishes.
  useEffect(() => {
    if (!navCue) return undefined;
    const id = setInterval(() => {
      const el = focusElRef.current;
      if (el && document.contains(el)) {
        if (!el.classList.contains("tvnav-foc")) el.classList.add("tvnav-foc");
        return;
      }
      const r = focusRectRef.current;
      if (!r) return;
      let best = null;
      let bestD = Infinity;
      navCandidates().forEach((c) => {
        const q = c.getBoundingClientRect();
        const d = Math.hypot((q.left + q.width / 2) - (r.left + r.width / 2), (q.top + q.height / 2) - (r.top + r.height / 2));
        if (d < bestD) { bestD = d; best = c; }
      });
      if (best) setFocusEl(best);
    }, 700);
    return () => clearInterval(id);
  }, [navCue, navCandidates, setFocusEl]);

  // Clear the ring whenever the cue goes away.
  useEffect(() => {
    if (navCue) return;
    document.querySelectorAll(".tvnav-foc").forEach((n) => n.classList.remove("tvnav-foc"));
    focusElRef.current = null;
    setFocusInBoard(false);
  }, [navCue]);

  useEffect(() => {
    const onKey = (e) => {
      // While the workspace switcher is open, it owns all keyboard input.
      if (switcherOpen) return;
      const k = e.key;
      const NAV_KEYS = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Enter", " ", "Escape", "Backspace", "w", "W"];
      if (!NAV_KEYS.includes(k)) return;

      // Quick-open the workspace switcher (unless typing in a field).
      if ((k === "w" || k === "W")) {
        if (e.target?.tagName !== "INPUT" && e.target?.tagName !== "TEXTAREA") {
          e.preventDefault();
          setSwitcherOpen(true);
        }
        return;
      }

      // Back: step up one area; from the top area (or with the cue already
      // hidden) it exits to home.
      if (k === "Escape" || k === "Backspace") {
        e.preventDefault();
        if (navCue) setNavCue(false); // put the cue away; the wall goes clean
        else window.location.replace(`${window.location.origin}/${BACK_TO_HOME}`);
        return;
      }

      e.preventDefault();
      // First press just wakes the cue and lands focus somewhere sensible.
      if (!navCue) {
        setNavCue(true);
        bumpIdle();
        const all = navCandidates();
        const board = all.find((el) => dashSceneRef.current && dashSceneRef.current.contains(el));
        setFocusEl(board || all[0]);
        return;
      }
      bumpIdle();

      if (k === "ArrowUp" || k === "ArrowDown" || k === "ArrowLeft" || k === "ArrowRight") {
        const dir = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right" }[k];
        const cur = focusElRef.current;
        const from = cur && document.contains(cur) ? cur.getBoundingClientRect() : focusRectRef.current;
        if (!from) { setFocusEl(navCandidates()[0]); return; }
        const scene = dashSceneRef.current;
        const inBoard = (el) => !!(scene && scene.contains(el));
        const vertical = dir === "up" || dir === "down";

        /* Going up/down while on the board: stay ON the board until it has
           genuinely run out. Next control on the board wins; if there's no
           control but there IS more board to reveal, scroll a step and keep
           the ring on the board; only a board that can't move any further
           hands off to the top bar (up) or the tab switcher (down). */
        if (vertical && focusInBoard && scene) {
          const nextOnBoard = pickInDirection(dir, from, inBoard);
          const step = dir === "down" ? 1 : -1;
          if (nextOnBoard) {
            // Don't jump past content that's still hidden below/above the fold.
            const s = scene.stageRect();
            const r = nextOnBoard.getBoundingClientRect();
            const offscreen = s && (r.top > s.bottom || r.bottom < s.top);
            if (offscreen && scene.canScroll(step)) { scene.nudge(step); return; }
            setFocusEl(nextOnBoard);
            return;
          }
          if (scene.canScroll(step)) {
            scene.nudge(step);
            // Re-anchor the ring onto something still visible on the board.
            const s = scene.stageRect();
            const visible = navCandidates().filter((el) => {
              if (!inBoard(el)) return false;
              const r = el.getBoundingClientRect();
              return s && r.top >= s.top && r.bottom <= s.bottom;
            });
            if (visible.length) setFocusEl(visible[dir === "down" ? visible.length - 1 : 0]);
            else if (scene.root()) setFocusEl(scene.root());
            return;
          }
          // Board is at the end — now leave it.
          const out = pickInDirection(dir, from, (el) => !inBoard(el));
          if (out) setFocusEl(out);
          return;
        }

        const next = pickInDirection(dir, from);
        if (next) { setFocusEl(next); return; }
        return;
      }
      if (k === "Enter" || k === " ") {
        const el = focusElRef.current;
        if (el && document.contains(el)) el.click();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [switcherOpen, navCue, bumpIdle, navCandidates, pickInDirection, setFocusEl, focusInBoard]);

  /* Workspace switch toast — shows "Switched to <name>" briefly when workspace changes */
  const [switchToast, setSwitchToast] = useState(null);
  const prevWorkspaceNameRef = useRef(workspace?.name);
  useEffect(() => {
    const next = workspace?.name;
    const prev = prevWorkspaceNameRef.current;
    if (next && prev && next !== prev) {
      setSwitchToast(next);
      const id = setTimeout(() => setSwitchToast(null), 2800);
      prevWorkspaceNameRef.current = next;
      return () => clearTimeout(id);
    }
    prevWorkspaceNameRef.current = next;
    return undefined;
  }, [workspace?.name]);

  const t = tokens(dark);

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        background: t.bg,
        color: t.text,
        fontFamily: FONT,
        fontVariantNumeric: "tabular-nums",
        overflow: "hidden",
      }}
    >
      <TopBar
        dark={dark}
        setDark={setDark}
        period={period}
        setPeriod={(p) => setPeriodIdx(PERIODS.indexOf(p))}
        paused={paused}
        menuOpen={switcherOpen}
        onMenu={() => setSwitcherOpen((v) => !v)}
        onHome={() => {
          window.location.replace(`${window.location.origin}/${BACK_TO_HOME}`);
        }}
      />

      {/* TV-native workspace switcher — a floating glass panel opened on demand
          from the brand+hamburger box. Overlays the board (never compresses it);
          changes workspace via redux without leaving /live/leaderboard/tv. */}
      <TVWorkspaceSwitcher
        open={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
        workspaces={workspaces || []}
        activeWorkspaceId={workspace?._id}
        accent="#5684d6"
        dark={dark}
        railLeftVh={1.4}
        playerCounts={playerCountsByWorkspace}
      />

      <Box
        key={sceneIdx}
        sx={{
          position: "absolute",
          inset: 0,
          animation: "tvSceneIn 480ms ease both",
        }}
      >
        {!activeBoard && sceneIdx === 0 && (
          <Scene1Leaderboard
            dark={dark}
            topPlayers={teamLeaderboard}
            kpiList={publicGroupKpis}
            officeStats={officeStats}
            period={period}
            yetiStats={yetiStats}
            lastMonthYetiBucket={lastMonthYetiBucket}
            workspaceName={workspace?.name || ""}
            perPlayer={!!workspace?.tvAveragePerPlayer}
          />
        )}
        {!activeBoard && sceneIdx === 1 && (
          <Scene2Pulse
            dark={dark}
            kpiTiles={kpiTiles}
            // Use the workspace-scoped roster (same source as Scene 1) so the
            // Workspace Champions podium ranks technicians by their position
            // inside the selected workspace, not their position company-wide.
            topPlayers={teamLeaderboard}
            workspace={workspace}
            period={period}
          />
        )}
        {activeBoard && (
          <TVDashboardScene
            ref={dashSceneRef}
            dashboard={activeBoard}
            dark={effectiveDark}
            workspaceName={workspace?.name || ""}
            navActive={navCue && focusInBoard}
          />
        )}
      </Box>

      {/* 2+ dashboards combined on this TV → the tab switcher owns the bottom.
          A single view keeps the scene-progress indicator (built-in's pages). */}
      {viewList.length > 1 ? (
        <ViewTabs
          tabs={tabMeta}
          activeId={viewId}
          dark={effectiveDark}
          onSelect={selectView}
        />
      ) : (pageCount > 1 && (
        <SceneIndicator
          index={sceneIdx}
          total={pageCount}
          dark={effectiveDark}
          paused={paused}
          label={sceneIdx === 0 ? "Leaderboard" : "Company Performance"}
        />
      ))}

      {/* Focus ring painted onto whatever [data-navfocus] element is active. */}
      <style>{`
        .tvnav-foc { outline: 3px solid #5684d6 !important; outline-offset: 3px;
          box-shadow: 0 0 0 6px rgba(86,132,214,0.22) !important;
          transition: outline-color 120ms ease, box-shadow 120ms ease; }
      `}</style>

      {/* Nav cue — a single glass pill: which area is focused + the key legend.
          Only shown while the cue is awake; sits above the tab bar. */}
      {navCue && (
        <Box
          sx={{
            position: "absolute",
            bottom: "7.2vh",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 30,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "1.1vw",
              px: "1.4vw",
              py: "0.9vh",
              borderRadius: "999px",
              background: dark ? "rgba(8,11,18,0.82)" : "rgba(255,255,255,0.9)",
              border: `1px solid ${dark ? "rgba(255,255,255,0.14)" : "rgba(15,23,42,0.12)"}`,
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              fontFamily: FONT,
              fontSize: "1.3vh",
              whiteSpace: "nowrap",
            }}
          >
            <Box component="span" sx={{ fontWeight: 700, color: "#5684d6" }}>
              Remote
            </Box>
            <Box component="span" sx={{ color: dark ? "rgba(236,237,237,0.7)" : "rgba(11,19,32,0.6)" }}>
              ↑↓←→ move &nbsp;·&nbsp; Enter select &nbsp;·&nbsp; Esc hide
            </Box>
          </Box>
        </Box>
      )}

      {/* Floating scene-toggle — middle-right edge.
          Manual switch between the leaderboard (sceneIdx 0) and the
          performance panel (sceneIdx 1). Visual language matches the
          workspace pill below: translucent glass surface, hairline border,
          backdrop blur. The icon hints at the destination scene so the
          button reads like a "go to other view" affordance. */}
      <Box
        component="button"
        type="button"
        onClick={goNext}
        aria-label={
          sceneIdx === 0
            ? "Switch to performance view"
            : "Switch to leaderboard view"
        }
        title={
          sceneIdx === 0
            ? "Switch to performance"
            : "Switch to leaderboard"
        }
        sx={{
          position: "absolute",
          top: "50%",
          right: "1.4vw",
          transform: "translateY(-50%)",
          width: "5.2vh",
          height: "5.2vh",
          padding: 0,
          borderRadius: "50%",
          background: dark ? "rgba(11,19,32,0.55)" : "rgba(255,255,255,0.7)",
          border: `1px solid ${t.border}`,
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          color: t.text,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          zIndex: 10,
          boxShadow: dark
            ? "0 6px 18px rgba(0,0,0,0.35)"
            : "0 6px 18px rgba(15,23,42,0.10)",
          transition: "transform 200ms ease, box-shadow 200ms ease, background 200ms ease",
          "&:hover": {
            transform: "translateY(-50%) scale(1.08)",
            background: dark ? "rgba(11,19,32,0.75)" : "rgba(255,255,255,0.9)",
          },
          "&:active": {
            transform: "translateY(-50%) scale(0.96)",
          },
          "&:focus-visible": {
            outline: `2px solid ${t.accent}`,
            outlineOffset: "2px",
          },
        }}
      >
        {sceneIdx === 0 ? (
          // → performance: a small bar chart pictograph
          <svg
            viewBox="0 0 24 24"
            width="2.4vh"
            height="2.4vh"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 20V10" />
            <path d="M10 20V4" />
            <path d="M16 20v-8" />
            <path d="M22 20H2" />
          </svg>
        ) : (
          // → leaderboard: a small ranked-list pictograph
          <svg
            viewBox="0 0 24 24"
            width="2.4vh"
            height="2.4vh"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M8 6h13" />
            <path d="M8 12h13" />
            <path d="M8 18h13" />
            <circle cx="4" cy="6" r="1" />
            <circle cx="4" cy="12" r="1" />
            <circle cx="4" cy="18" r="1" />
          </svg>
        )}
      </Box>

      {/* Persistent workspace pill — bottom-right channel-logo style */}
      {workspace?.name && (
        <Box
          sx={{
            position: "absolute",
            bottom: "1.6vh",
            right: "1.4vw",
            display: "flex",
            alignItems: "center",
            gap: "0.5vw",
            px: "0.9vw",
            py: "0.6vh",
            borderRadius: "999px",
            background: dark ? "rgba(11,19,32,0.55)" : "rgba(255,255,255,0.7)",
            border: `1px solid ${t.border}`,
            backdropFilter: "blur(8px)",
            zIndex: 9,
          }}
        >
          <Box
            sx={{
              width: "0.7vh",
              height: "0.7vh",
              borderRadius: "50%",
              background: t.accent,
              boxShadow: `0 0 8px ${t.accent}`,
            }}
          />
          <Box
            component="span"
            sx={{
              fontFamily: FONT,
              fontWeight: 500,
              fontSize: "1vh",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: t.textMuted,
            }}
          >
            On Air ·
          </Box>
          <Box
            component="span"
            sx={{
              fontFamily: FONT,
              fontWeight: 500,
              fontSize: "1.1vh",
              letterSpacing: "-0.005em",
              color: t.text,
            }}
          >
            {workspace.name}
          </Box>
        </Box>
      )}

      {/* Workspace switch toast — center-top */}
      {switchToast && (
        <Box
          key={switchToast}
          sx={{
            position: "absolute",
            top: "11vh",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: "0.7vw",
            px: "1.6vw",
            py: "1.2vh",
            borderRadius: "14px",
            background: dark
              ? "linear-gradient(180deg, rgba(20,32,55,0.95) 0%, rgba(11,19,32,0.95) 100%)"
              : "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(244,247,252,0.95) 100%)",
            border: `1px solid ${t.accent}66`,
            boxShadow: `0 12px 36px rgba(0,0,0,0.35), 0 0 0 1px ${t.accent}22`,
            backdropFilter: "blur(12px)",
            animation: "tvToastIn 320ms cubic-bezier(0.2,0.8,0.2,1) both",
            zIndex: 20,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              width: "1vh",
              height: "1vh",
              borderRadius: "50%",
              background: t.accent,
              boxShadow: `0 0 12px ${t.accent}`,
            }}
          />
          <Box
            component="span"
            sx={{
              fontFamily: FONT,
              fontWeight: 500,
              fontSize: "1.1vh",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: t.textMuted,
            }}
          >
            Switched to
          </Box>
          <Box
            component="span"
            sx={{
              fontFamily: FONT,
              fontWeight: 500,
              fontSize: "2vh",
              letterSpacing: "-0.005em",
              color: t.text,
            }}
          >
            {switchToast}
          </Box>
        </Box>
      )}

      <style>
        {`
          @keyframes tvSceneIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes tvToastIn {
            from { opacity: 0; transform: translate(-50%, -8px) scale(0.96); }
            to   { opacity: 1; transform: translate(-50%, 0) scale(1); }
          }
        `}
      </style>
    </Box>
  );
};

export default TVLeaderboard;
